import { z } from 'zod'
import type { AreEquivalent } from '../../types'
import type { components } from './openapi-spec'

// #region Base schemas (mapped to OpenAPI, required by utility schemas)

export const HALLinkSchema = z
  .object({
    href: z.string(),
    title: z.string().optional(),
  })
  .describe('HALLink')
export type HALLink = z.infer<typeof HALLinkSchema>
true satisfies AreEquivalent<HALLink, components['schemas']['HALLink']>

export const PatchesPatchFixLinksSchema = z.object({
  'fix-result': HALLinkSchema,
  self: HALLinkSchema,
})
export type PatchesPatchFixLinks = z.infer<typeof PatchesPatchFixLinksSchema>
true satisfies AreEquivalent<PatchesPatchFixLinks, components['schemas']['PatchesPatchFixLinks']>

export const SelfLinkSchema = z
  .object({
    self: HALLinkSchema,
  })
  .describe('SelfLink')
export type SelfLink = z.infer<typeof SelfLinkSchema>
true satisfies AreEquivalent<SelfLink, components['schemas']['SelfLink']>

export const StandardPageNavigationLinksSchema = z
  .object({
    first: HALLinkSchema,
    next: HALLinkSchema.optional(),
    prev: HALLinkSchema.optional(),
    self: HALLinkSchema,
  })
  .describe('StandardPageNavigationLinks')
export type StandardPageNavigationLinks = z.infer<typeof StandardPageNavigationLinksSchema>
true satisfies AreEquivalent<StandardPageNavigationLinks, components['schemas']['StandardPageNavigationLinks']>

const PageResponseSchema = z.object({
  number: z.number().int(),
  size: z.number().int(),
})
type PageResponse = z.infer<typeof PageResponseSchema>
true satisfies AreEquivalent<PageResponse, components['schemas']['PageResponse']>

// #endregion

// #region Utility schemas (no OpenAPI counterpart)

/**
 * Schema with a permissive input type and a strict output type. It simply filters out items that don't match the strict schema.
 *
 * Accepts any `ZodType<unknown>` so callers can pass either a top-level `z.discriminatedUnion(...)` (the typical case) or a
 * plain `z.object(...)` whose discriminator is nested (e.g. `WorkflowResponseSchema` discriminates on `event.type`). The
 * outer schema is `z.unknown()` because per-item `safeParse` against `itemSchema` is the real narrowing step.
 */
function PermissiveDiscriminatedUnionArray<TSchema extends z.ZodType>(itemSchema: TSchema) {
  return (
    z
      .array(z.unknown())
      // As a future improvement, we could update this to support both optional and non-optional arrays.
      .optional()
      .nullable()
      .transform(items =>
        items
          ?.map(item => itemSchema.safeParse(item))
          .filter((result): result is z.ZodSafeParseSuccess<z.infer<TSchema>> => result.success)
          .map(result => result.data)
      )
      .optional()
  )
}

const Paginated = <T extends z.ZodType, TLinks extends z.ZodType>(itemSchema: T, linksSchema: TLinks) =>
  z.object({
    _embedded: z.object({
      items: z.array(itemSchema),
    }),
    _links: linksSchema,
    page: PageResponseSchema,
    total: z.number().int(),
  })

const PaginatedResponse = Paginated(z.unknown(), StandardPageNavigationLinksSchema)
export type PaginatedResponse = z.infer<typeof PaginatedResponse>

// #endregion

// #region API schemas

export const FindingSortFieldSchema = z
  .enum(['pixee-intelligence', 'pixee-severity', 'severity'])
  .describe('FindingSortField')
export type FindingSortField = z.infer<typeof FindingSortFieldSchema>
true satisfies AreEquivalent<FindingSortField, components['schemas']['FindingSortField']>

export const SortOrderSchema = z.enum(['asc', 'desc']).describe('SortOrder')
export type SortOrder = z.infer<typeof SortOrderSchema>
true satisfies AreEquivalent<SortOrder, components['schemas']['SortOrder']>

export const UserSchema = z
  .object({
    avatar_url: z.string(),
    email: z.string(),
    login: z.string(),
    name: z.string().nullable(),
  })
  .describe('User')
export type User = z.infer<typeof UserSchema>

export const CurrentUserResponseSchema = z
  .object({
    account_management_url: z.string().optional(),
    avatar_url: z.string().optional(),
    email: z.string().optional(),
    login: z.string().optional(),
    roles: z.array(z.string()).optional(),
    username: z.string().optional(),
  })
  .describe('CurrentUserResponse')
export type CurrentUserResponse = z.infer<typeof CurrentUserResponseSchema>
true satisfies AreEquivalent<CurrentUserResponse, components['schemas']['CurrentUserResponse']>

export const SystemStatusStatusSchema = z.enum(['up', 'down']).describe('SystemStatusStatus')
export type SystemStatusStatus = z.infer<typeof SystemStatusStatusSchema>
true satisfies AreEquivalent<SystemStatusStatus, components['schemas']['SystemStatusValue']>

export const AIModelStatusResponseSchema = z
  .object({
    _links: SelfLinkSchema,
    description: z.string().optional(),
    id: z.string(),
    model: z.string(),
    name: z.string(),
    status: SystemStatusStatusSchema,
    type: z.literal('ai-model'),
  })
  .describe('AIModelStatusResponse')
export type AIModelStatusResponse = z.infer<typeof AIModelStatusResponseSchema>
true satisfies AreEquivalent<AIModelStatusResponse, components['schemas']['AIModelStatusResponse']>

export const LicenseStatusResponseSchema = z
  .object({
    _links: SelfLinkSchema,
    description: z.string().optional(),
    // The backend emits expires_at as an empty string on license-failure / no-expiry paths, so it is
    // not always an ISO-8601 timestamp. Keep it as z.string(); do NOT tighten to .datetime().
    expires_at: z.string().optional(),
    features_enabled: z.boolean().optional(),
    id: z.string(),
    name: z.string(),
    status: SystemStatusStatusSchema,
    type: z.literal('license'),
  })
  .describe('LicenseStatusResponse')
export type LicenseStatusResponse = z.infer<typeof LicenseStatusResponseSchema>
true satisfies AreEquivalent<LicenseStatusResponse, components['schemas']['LicenseStatusResponse']>

export const VersionStatusResponseSchema = z
  .object({
    _links: SelfLinkSchema,
    description: z.string().optional(),
    id: z.string(),
    name: z.string(),
    status: SystemStatusStatusSchema,
    type: z.literal('version'),
    version: z.string(),
  })
  .describe('VersionStatusResponse')
export type VersionStatusResponse = z.infer<typeof VersionStatusResponseSchema>
true satisfies AreEquivalent<VersionStatusResponse, components['schemas']['VersionStatusResponse']>

export const SystemStatusSchema = z
  .discriminatedUnion('type', [AIModelStatusResponseSchema, LicenseStatusResponseSchema, VersionStatusResponseSchema])
  .describe('SystemStatus')
export type SystemStatus = z.infer<typeof SystemStatusSchema>
true satisfies AreEquivalent<SystemStatus, components['schemas']['SystemStatusResponse']>

export const SystemStatusesResponseSchema = z
  .object({
    _embedded: z.object({
      items: PermissiveDiscriminatedUnionArray(SystemStatusSchema),
    }),
    _links: StandardPageNavigationLinksSchema,
    page: PageResponseSchema,
    total: z.number().int(),
  })
  .describe('SystemStatusesResponse')
export type SystemStatusesResponse = z.infer<typeof SystemStatusesResponseSchema>
// @ts-expect-error PermissiveDiscriminatedUnionArray types the embedded items array as optional, but the spec's PageEmbeddedSystemStatusResponse requires it.
true satisfies AreEquivalent<SystemStatusesResponse, components['schemas']['SystemStatusPaginatedResponse']>

export const RepositoryTypeSchema = z.enum(['git', 'azure', 'github', 'gitlab', 'bitbucket']).describe('RepositoryType')
export type RepositoryType = z.infer<typeof RepositoryTypeSchema>
true satisfies AreEquivalent<RepositoryType, components['schemas']['RepositoryResponse']['type']>
export const RepositoryLinksSchema = z
  .object({
    'latest-analysis': HALLinkSchema.optional(),
    'latest-scan': HALLinkSchema.optional(),
    'repository-integrations': HALLinkSchema.optional(),
    scans: HALLinkSchema.optional(),
    self: HALLinkSchema,
    workflows: HALLinkSchema.optional(),
  })
  .describe('RepositoryLinks')
export type RepositoryLinks = z.infer<typeof RepositoryLinksSchema>
true satisfies AreEquivalent<RepositoryLinks, components['schemas']['RepositoryLinks']>
const BaseRepositorySchema = z.object({
  _links: RepositoryLinksSchema,
  default_branch: z.string(),
  full_name: z.string(),
  id: z.string(),
  name: z.string(),
})
const AzureRepositorySchema = BaseRepositorySchema.extend({
  organization: z.string(),
  project: z.string(),
  type: z.literal('azure'),
})
type AzureRepository = z.infer<typeof AzureRepositorySchema>
true satisfies AreEquivalent<AzureRepository, components['schemas']['RepositoriesAzureRepositoryResponse']>
const GitRepositorySchema = BaseRepositorySchema.extend({
  type: z.literal('git'),
})
type GitRepository = z.infer<typeof GitRepositorySchema>
true satisfies AreEquivalent<GitRepository, components['schemas']['GitRepositoryResponse']>
const GitHubRepositorySchema = BaseRepositorySchema.extend({
  html_url: z.string(),
  owner: z.string(),
  type: z.literal('github'),
})
type GitHubRepository = z.infer<typeof GitHubRepositorySchema>
true satisfies AreEquivalent<GitHubRepository, components['schemas']['GitHubAppRepositoryResponse']>
const GitLabRepositorySchema = BaseRepositorySchema.extend({
  namespace: z.string(),
  type: z.literal('gitlab'),
})
type GitLabRepository = z.infer<typeof GitLabRepositorySchema>
true satisfies AreEquivalent<GitLabRepository, components['schemas']['GitLabRepositoryResponse']>
const BitbucketRepositorySchema = BaseRepositorySchema.extend({
  slug: z.string(),
  type: z.literal('bitbucket'),
  workspace: z.string(),
})
type BitbucketRepository = z.infer<typeof BitbucketRepositorySchema>
true satisfies AreEquivalent<BitbucketRepository, components['schemas']['RepositoriesBitbucketRepositoryResponse']>
export const RepositorySchema = z
  .discriminatedUnion('type', [
    GitHubRepositorySchema,
    GitLabRepositorySchema,
    AzureRepositorySchema,
    BitbucketRepositorySchema,
    GitRepositorySchema,
  ])
  .describe('Repository')
export type Repository = z.infer<typeof RepositorySchema>
true satisfies AreEquivalent<Repository, components['schemas']['RepositoryResponse']>

export const RepositoryPaginatedResponseSchema = Paginated(
  RepositorySchema,
  StandardPageNavigationLinksSchema
).describe('RepositoryPaginatedResponse')
export type RepositoryPaginatedResponse = z.infer<typeof RepositoryPaginatedResponseSchema>
true satisfies AreEquivalent<RepositoryPaginatedResponse, components['schemas']['RepositoryPaginatedResponse']>

// Tool represents the lowercase enum from the OpenAPI spec (used in workflow.tool, createScan.metadata.tool)
export const ToolSchema = z
  .enum([
    'aikido_sast',
    'aikido_sca',
    'codeql',
    'sonar',
    'semgrep',
    'polaris_sast',
    'polaris_sca',
    'appscan',
    'defectdojo',
    'datadog_sast',
    'arnica_sast',
    'dependabot',
    'contrast',
    'gitlab_sast',
    'gitlab_dependency_scanning',
    'snyk',
    'checkmarx',
    'veracode',
    'fortify',
    'gitleaks',
    'zap',
    // Demo-only: the Antares mockup surfaces as a real tool so the RC repository-details tool
    // selector defaults to it cleanly. Not in the OpenAPI spec — parity check suppressed below.
    'antares',
  ])
  .describe('Tool')
export type Tool = z.infer<typeof ToolSchema>
// @ts-expect-error 'antares' is a demo-only enum value not in the OpenAPI spec (see comment above).
true satisfies AreEquivalent<Tool, components['schemas']['Tool']>

// Instant is inlined (string) in the regenerated spec; kept as a named schema for readability.
export const InstantSchema = z.string().describe('Instant')
export type Instant = z.infer<typeof InstantSchema>

export const ScanAnalysisStateSchema = z
  .enum(['completed', 'in-progress', 'not-analyzed'])
  .describe('ScanAnalysisState')
export type ScanAnalysisState = z.infer<typeof ScanAnalysisStateSchema>
true satisfies AreEquivalent<ScanAnalysisState, components['schemas']['ScanAnalysisState']>

export const ScanLinksSchema = z
  .object({
    analyses: HALLinkSchema.optional(),
    'analysis-cancellations': HALLinkSchema.optional(),
    findings: HALLinkSchema,
    integration: HALLinkSchema.optional(),
    patches: HALLinkSchema,
    repository: HALLinkSchema,
    scale: HALLinkSchema,
    self: HALLinkSchema,
  })
  .describe('ScanLinks')
export type ScanLinks = z.infer<typeof ScanLinksSchema>
true satisfies AreEquivalent<ScanLinks, components['schemas']['ScanLinks']>

export const SbomToolSchema = z
  .enum(['aikido_sca', 'dependabot', 'gitlab_dependency_scanning', 'polaris_sca'])
  .describe('SbomTool')
export type SbomTool = z.infer<typeof SbomToolSchema>
true satisfies AreEquivalent<SbomTool, components['schemas']['SbomTool']>

export const SbomLinksSchema = z
  .object({
    repository: HALLinkSchema,
    self: HALLinkSchema,
  })
  .describe('SbomLinks')
export type SbomLinks = z.infer<typeof SbomLinksSchema>
true satisfies AreEquivalent<SbomLinks, components['schemas']['SbomLinks']>

export const SbomResponseSchema = z
  .object({
    _links: SbomLinksSchema,
    branch: z.string().optional(),
    created_at: InstantSchema,
    id: z.string(),
    sha: z.string(),
  })
  .describe('SbomResponse')
export type SbomResponse = z.infer<typeof SbomResponseSchema>
true satisfies AreEquivalent<SbomResponse, components['schemas']['SbomResponse']>

// Standalone `ThreatModelStatus` — used on both `ThreatModelResponse.status` and
// `RefreshRequestResponse.threat_model_document_status`, where analysis-service may report
// `unknown` when it can't determine the live status.
export const ThreatModelStatusSchema = z
  .enum(['processing', 'completed', 'failed', 'unknown'])
  .describe('ThreatModelStatus')
export type ThreatModelStatus = z.infer<typeof ThreatModelStatusSchema>
true satisfies AreEquivalent<ThreatModelStatus, components['schemas']['ThreatModelStatus']>

export const ThreatModelPhaseSchema = z
  .enum(['queued', 'fetching_code', 'exploring_codebase', 'modeling_threats', 'rendering', 'unknown'])
  .describe('ThreatModelPhase')
export type ThreatModelPhase = z.infer<typeof ThreatModelPhaseSchema>
true satisfies AreEquivalent<ThreatModelPhase, components['schemas']['ThreatModelPhase']>

// `processing` and `unknown` are both in-flight generation states: analysis-service reports
// `unknown` when it can't determine the live status, so callers must keep streaming/polling until it
// resolves to a terminal `completed`/`failed`. Shared by both generation tabs and the polling gate
// in `api-hooks.ts` so the render, the stream gate, and the poll gate can't disagree.
export const isInFlightGenerationStatus = (status: string): boolean => status === 'processing' || status === 'unknown'

export const ThreatModelLinksSchema = z
  .object({
    content: HALLinkSchema.optional(),
    events: HALLinkSchema.optional(),
    self: HALLinkSchema,
  })
  .describe('ThreatModelLinks')
export type ThreatModelLinks = z.infer<typeof ThreatModelLinksSchema>
true satisfies AreEquivalent<ThreatModelLinks, components['schemas']['ThreatModelLinks']>

export const ThreatModelResponseSchema = z
  .object({
    _links: ThreatModelLinksSchema,
    commitSha: z.string().optional(),
    failureReason: z.string().optional(),
    generatedAt: InstantSchema.optional(),
    phase: ThreatModelPhaseSchema,
    requestedBy: z.string().optional(),
    status: ThreatModelStatusSchema,
  })
  .describe('ThreatModelResponse')
export type ThreatModelResponse = z.infer<typeof ThreatModelResponseSchema>
true satisfies AreEquivalent<ThreatModelResponse, components['schemas']['ThreatModelResponse']>

export const ThreatModelUnavailableProblemSchema = z
  .object({
    detail: z.string(),
    status: z.number().int(),
    title: z.string(),
  })
  .describe('ThreatModelUnavailableProblem')
export type ThreatModelUnavailableProblem = z.infer<typeof ThreatModelUnavailableProblemSchema>
true satisfies AreEquivalent<ThreatModelUnavailableProblem, components['schemas']['ThreatModelUnavailableProblem']>

// The threat-model `/content` endpoint returns the analysis-service `model_json` document. The
// platform forwards that body byte-for-byte (the caller's `Accept: application/json` header selects
// JSON over the legacy markdown rendering), so the wire shape is analysis-service's, expressed in
// snake_case, and this schema matches it verbatim. Like `FindingOverlayResponse` above, there is no
// `components['schemas']` counterpart to assert against — the platform forwards the body opaquely
// rather than re-declaring it in its OpenAPI spec — so no `AreEquivalent` check is added. Categorical
// fields (`trust_level`, `kind`, `relevance`, …) are typed as `z.string()` rather than enums: the
// values are generated per model and the UI degrades gracefully on ones it does not recognise.
export const ThreatModelActorSchema = z
  .object({
    authenticates_via: z.string(),
    can_reach: z.string(),
    name: z.string(),
    trust_level: z.string(),
  })
  .describe('ThreatModelActor')
export type ThreatModelActor = z.infer<typeof ThreatModelActorSchema>

export const ThreatModelExposureSchema = z
  .object({
    deployment_environment: z.string(),
    internet_facing: z.string(),
    network_position: z.string(),
    notes: z.string(),
  })
  .describe('ThreatModelExposure')
export type ThreatModelExposure = z.infer<typeof ThreatModelExposureSchema>

export const ThreatModelEntryPointSchema = z
  .object({
    authentication: z.string(),
    description: z.string(),
    kind: z.string(),
    location: z.string(),
    name: z.string(),
  })
  .describe('ThreatModelEntryPoint')
export type ThreatModelEntryPoint = z.infer<typeof ThreatModelEntryPointSchema>

export const ThreatModelTrustBoundarySchema = z
  .object({
    crosses: z.string(),
    description: z.string(),
    name: z.string(),
  })
  .describe('ThreatModelTrustBoundary')
export type ThreatModelTrustBoundary = z.infer<typeof ThreatModelTrustBoundarySchema>

export const ThreatModelSensitiveAssetSchema = z
  .object({
    description: z.string(),
    kind: z.string(),
    location: z.string(),
    name: z.string(),
  })
  .describe('ThreatModelSensitiveAsset')
export type ThreatModelSensitiveAsset = z.infer<typeof ThreatModelSensitiveAssetSchema>

export const ThreatModelSecurityControlSchema = z
  .object({
    kind: z.string(),
    location: z.string(),
    mitigates: z.string().array(),
    name: z.string(),
    scope: z.string(),
  })
  .describe('ThreatModelSecurityControl')
export type ThreatModelSecurityControl = z.infer<typeof ThreatModelSecurityControlSchema>

export const ThreatModelDependencyNoteSchema = z
  .object({
    location: z.string(),
    name: z.string(),
    reason: z.string(),
  })
  .describe('ThreatModelDependencyNote')
export type ThreatModelDependencyNote = z.infer<typeof ThreatModelDependencyNoteSchema>

export const ThreatModelVulnerabilityImpactSchema = z
  .object({
    blast_radius: z.string(),
    mitigating_controls: z.string(),
    reachability: z.string(),
    relevance: z.string(),
    verify: z.string(),
    vuln_class: z.string(),
  })
  .describe('ThreatModelVulnerabilityImpact')
export type ThreatModelVulnerabilityImpact = z.infer<typeof ThreatModelVulnerabilityImpactSchema>

export const ThreatModelContentSchema = z
  .object({
    actors: ThreatModelActorSchema.array(),
    assumptions: z.string().array(),
    attack_surface_summary: z.string(),
    authentication_model: z.string(),
    authorization_model: z.string(),
    data_flow_notes: z.string(),
    dependency_notes: ThreatModelDependencyNoteSchema.array(),
    entry_points: ThreatModelEntryPointSchema.array(),
    exposure: ThreatModelExposureSchema,
    security_controls: ThreatModelSecurityControlSchema.array(),
    sensitive_assets: ThreatModelSensitiveAssetSchema.array(),
    system_overview: z.string(),
    trust_boundaries: ThreatModelTrustBoundarySchema.array(),
    vulnerability_class_impacts: ThreatModelVulnerabilityImpactSchema.array(),
  })
  .describe('ThreatModelContent')
export type ThreatModelContent = z.infer<typeof ThreatModelContentSchema>

// `threat_model_document_status` reuses the standalone `ThreatModelStatus` component (asserted
// above); `state` and `trigger_source` are standalone spec components with their own
// `AreEquivalent` assertions below.
export const RefreshRequestStateResponseSchema = z
  .enum(['pending', 'claimed', 'regenerating', 'completed', 'failed', 'no-revision', 'superseded'])
  .describe('RefreshRequestStateResponse')
export type RefreshRequestStateResponse = z.infer<typeof RefreshRequestStateResponseSchema>
true satisfies AreEquivalent<RefreshRequestStateResponse, components['schemas']['RefreshRequestStateResponse']>

export const RefreshTriggerSourceResponseSchema = z
  .enum(['onboarding', 'direct-push', 'merged-pr'])
  .describe('RefreshTriggerSourceResponse')
export type RefreshTriggerSourceResponse = z.infer<typeof RefreshTriggerSourceResponseSchema>
true satisfies AreEquivalent<RefreshTriggerSourceResponse, components['schemas']['RefreshTriggerSourceResponse']>

export const RefreshRequestLinksSchema = z
  .object({
    commit: HALLinkSchema.optional(),
    'pull-request': HALLinkSchema.optional(),
    repository: HALLinkSchema,
    self: HALLinkSchema,
    'superseded-by': HALLinkSchema.optional(),
    'threat-model': HALLinkSchema,
  })
  .describe('RefreshRequestLinks')
export type RefreshRequestLinks = z.infer<typeof RefreshRequestLinksSchema>
true satisfies AreEquivalent<RefreshRequestLinks, components['schemas']['RefreshRequestLinks']>

export const RefreshRequestResponseSchema = z
  .object({
    _links: RefreshRequestLinksSchema,
    after_sha: z.string().optional(),
    before_sha: z.string().optional(),
    decision_rationale: z.string().optional(),
    id: z.string(),
    pull_request_number: z.number().int().optional(),
    ref: z.string().optional(),
    state: RefreshRequestStateResponseSchema,
    threat_model_document_id: z.string().optional(),
    threat_model_document_status: ThreatModelStatusSchema.optional(),
    trigger_source: RefreshTriggerSourceResponseSchema,
    triggered_at: InstantSchema,
  })
  .describe('RefreshRequestResponse')
export type RefreshRequestResponse = z.infer<typeof RefreshRequestResponseSchema>
true satisfies AreEquivalent<RefreshRequestResponse, components['schemas']['RefreshRequestResponse']>

export const RefreshRequestsLinksSchema = z
  .object({
    repository: HALLinkSchema,
    self: HALLinkSchema,
    'threat-model': HALLinkSchema,
  })
  .describe('RefreshRequestsLinks')
export type RefreshRequestsLinks = z.infer<typeof RefreshRequestsLinksSchema>
true satisfies AreEquivalent<RefreshRequestsLinks, components['schemas']['RefreshRequestsLinks']>

export const RefreshRequestsResponseSchema = z
  .object({
    _links: RefreshRequestsLinksSchema,
    items: z.array(RefreshRequestResponseSchema),
    next_cursor: z.string().optional(),
  })
  .describe('RefreshRequestsResponse')
export type RefreshRequestsResponse = z.infer<typeof RefreshRequestsResponseSchema>
true satisfies AreEquivalent<RefreshRequestsResponse, components['schemas']['RefreshRequestsResponse']>

export const ExploitChainsStatusSchema = z
  .enum(['processing', 'completed', 'failed', 'unknown'])
  .describe('ExploitChainsStatus')
export type ExploitChainsStatus = z.infer<typeof ExploitChainsStatusSchema>
true satisfies AreEquivalent<ExploitChainsStatus, components['schemas']['ExploitChainsStatus']>

export const ExploitChainsPhaseSchema = z
  .enum(['queued', 'fetching_code', 'loading_inputs', 'correlating', 'rendering', 'unknown'])
  .describe('ExploitChainsPhase')
export type ExploitChainsPhase = z.infer<typeof ExploitChainsPhaseSchema>
true satisfies AreEquivalent<ExploitChainsPhase, components['schemas']['ExploitChainsPhase']>

export const ExploitChainsLinksSchema = z
  .object({
    content: HALLinkSchema.optional(),
    events: HALLinkSchema.optional(),
    self: HALLinkSchema,
  })
  .describe('ExploitChainsLinks')
export type ExploitChainsLinks = z.infer<typeof ExploitChainsLinksSchema>
true satisfies AreEquivalent<ExploitChainsLinks, components['schemas']['ExploitChainsLinks']>

export const FindingBackreferenceLinksSchema = z
  .object({
    finding: HALLinkSchema,
    self: HALLinkSchema,
  })
  .describe('FindingBackreferenceLinks')
export type FindingBackreferenceLinks = z.infer<typeof FindingBackreferenceLinksSchema>
true satisfies AreEquivalent<FindingBackreferenceLinks, components['schemas']['FindingBackreferenceLinks']>

export const LinkedFindingSchema = z
  .object({
    _links: FindingBackreferenceLinksSchema.optional(),
    findingId: z.string(),
    fixAvailable: z.boolean().optional(),
    kind: z.string().optional(),
    path: z.string().optional(),
    rule: z.string().optional(),
    severity: z.string().optional(),
    title: z.string().optional(),
    tool: z.string().optional(),
  })
  .describe('LinkedFinding')
export type LinkedFinding = z.infer<typeof LinkedFindingSchema>
true satisfies AreEquivalent<LinkedFinding, components['schemas']['LinkedFinding']>

export const ChainSummarySchema = z
  .object({
    chainId: z.string(),
    detailMarkdown: z.string().optional(),
    linkedFindings: LinkedFindingSchema.array().optional(),
    severity: z.string().optional(),
    stepCount: z.number().int().optional(),
    title: z.string(),
  })
  .describe('ChainSummary')
export type ChainSummary = z.infer<typeof ChainSummarySchema>
true satisfies AreEquivalent<ChainSummary, components['schemas']['ChainSummary']>

export const ExploitChainsResponseSchema = z
  .object({
    _links: ExploitChainsLinksSchema,
    chains: ChainSummarySchema.array().optional(),
    commitSha: z.string().optional(),
    failureReason: z.string().optional(),
    generatedAt: InstantSchema.optional(),
    phase: ExploitChainsPhaseSchema,
    requestedBy: z.string().optional(),
    status: ExploitChainsStatusSchema,
  })
  .describe('ExploitChainsResponse')
export type ExploitChainsResponse = z.infer<typeof ExploitChainsResponseSchema>
true satisfies AreEquivalent<ExploitChainsResponse, components['schemas']['ExploitChainsResponse']>

export const ExploitChainsUnavailableProblemSchema = z
  .object({
    detail: z.string(),
    status: z.number().int(),
    title: z.string(),
  })
  .describe('ExploitChainsUnavailableProblem')
export type ExploitChainsUnavailableProblem = z.infer<typeof ExploitChainsUnavailableProblemSchema>
true satisfies AreEquivalent<ExploitChainsUnavailableProblem, components['schemas']['ExploitChainsUnavailableProblem']>

// #region Finding Overlays

// Every categorical field carries `unknown`, exactly as the spec declares it: analysis-service
// reports it when it can't determine the value, so a schema without it rejects a legal payload and
// takes the whole tab down with it. Same reasoning as `ThreatModelStatus` above, and
// `isInFlightGenerationStatus` treats `unknown` as still-running for the poll and stream gates.
export const FindingOverlayStatusSchema = z
  .enum(['processing', 'completed', 'failed', 'unknown'])
  .describe('FindingOverlayStatus')
export type FindingOverlayStatus = z.infer<typeof FindingOverlayStatusSchema>
true satisfies AreEquivalent<FindingOverlayStatus, components['schemas']['FindingOverlayStatus']>

export const FindingOverlayPhaseSchema = z.enum(['queued', 'analyzing', 'unknown']).describe('FindingOverlayPhase')
export type FindingOverlayPhase = z.infer<typeof FindingOverlayPhaseSchema>
true satisfies AreEquivalent<FindingOverlayPhase, components['schemas']['FindingOverlayPhase']>

export const FindingOverlayTierSchema = z
  .enum(['critical', 'high', 'medium', 'low', 'unknown'])
  .describe('FindingOverlayTier')
export type FindingOverlayTier = z.infer<typeof FindingOverlayTierSchema>
true satisfies AreEquivalent<FindingOverlayTier, components['schemas']['FindingOverlayTier']>

export const FindingOverlayDimensionSchema = z
  .enum(['entry_point', 'actor', 'trust_boundary', 'sensitive_asset', 'vulnerability_class', 'unknown'])
  .describe('FindingOverlayDimension')
export type FindingOverlayDimension = z.infer<typeof FindingOverlayDimensionSchema>
true satisfies AreEquivalent<FindingOverlayDimension, components['schemas']['FindingOverlayDimension']>

export const FindingOverlaysLinksSchema = z
  .object({
    content: HALLinkSchema.optional(),
    events: HALLinkSchema.optional(),
    self: HALLinkSchema,
  })
  .describe('FindingOverlaysLinks')
export type FindingOverlaysLinks = z.infer<typeof FindingOverlaysLinksSchema>
true satisfies AreEquivalent<FindingOverlaysLinks, components['schemas']['FindingOverlaysLinks']>

export const FindingOverlaySummarySchema = z
  .object({
    clusterCount: z.number().int(),
    highestTier: FindingOverlayTierSchema.optional(),
    priorityCount: z.number().int(),
  })
  .describe('FindingOverlaySummary')
export type FindingOverlaySummary = z.infer<typeof FindingOverlaySummarySchema>
true satisfies AreEquivalent<FindingOverlaySummary, components['schemas']['FindingOverlaySummary']>

export const FindingOverlaysResponseSchema = z
  .object({
    _links: FindingOverlaysLinksSchema,
    failureReason: z.string().optional(),
    generatedAt: InstantSchema.optional(),
    phase: FindingOverlayPhaseSchema,
    requestedBy: z.string().optional(),
    status: FindingOverlayStatusSchema,
    summary: FindingOverlaySummarySchema.optional(),
  })
  .describe('FindingOverlaysResponse')
export type FindingOverlaysResponse = z.infer<typeof FindingOverlaysResponseSchema>
true satisfies AreEquivalent<FindingOverlaysResponse, components['schemas']['FindingOverlaysResponse']>

/**
 * A finding reference embedded in overlay content, sharing `FindingBackreferenceLinks` with the
 * exploit-chain `LinkedFinding` above rather than re-declaring HAL links per feature.
 *
 * The whole `_links` object is absent — not just `finding` inside it — when analysis-service's
 * backreference could not be resolved, and the enrichment fields (`rule/path/tool/severity`) go
 * with it. Consumers render an unlinked `findingId` label in that case rather than failing the row.
 */
export const FindingOverlayClusterFindingSchema = z
  .object({
    _links: FindingBackreferenceLinksSchema.optional(),
    findingId: z.string(),
    path: z.string().optional(),
    rule: z.string().optional(),
    severity: z.string().optional(),
    tool: z.string().optional(),
  })
  .describe('FindingOverlayClusterFinding')
export type FindingOverlayClusterFinding = z.infer<typeof FindingOverlayClusterFindingSchema>
true satisfies AreEquivalent<FindingOverlayClusterFinding, components['schemas']['FindingOverlayClusterFinding']>

// `rationale` is optional on both the priority and the cluster: the agent cites the threat-model
// element that moved the ranking when it has one to cite, and omits the field otherwise.
export const FindingOverlayPrioritySchema = FindingOverlayClusterFindingSchema.extend({
  rationale: z.string().optional(),
  score: z.number().int().min(0).max(100),
  tier: FindingOverlayTierSchema,
}).describe('FindingOverlayPriority')
export type FindingOverlayPriority = z.infer<typeof FindingOverlayPrioritySchema>
true satisfies AreEquivalent<FindingOverlayPriority, components['schemas']['FindingOverlayPriority']>

export const FindingOverlayClusterSchema = z
  .object({
    dimension: FindingOverlayDimensionSchema,
    findings: FindingOverlayClusterFindingSchema.array(),
    name: z.string(),
    rationale: z.string().optional(),
    subjectName: z.string(),
  })
  .describe('FindingOverlayCluster')
export type FindingOverlayCluster = z.infer<typeof FindingOverlayClusterSchema>
true satisfies AreEquivalent<FindingOverlayCluster, components['schemas']['FindingOverlayCluster']>

export const FindingOverlayResponseSchema = z
  .object({
    assumptions: z.string().array(),
    clusters: FindingOverlayClusterSchema.array(),
    narrative: z.string(),
    priorities: FindingOverlayPrioritySchema.array(),
  })
  .describe('FindingOverlayResponse')
export type FindingOverlayResponse = z.infer<typeof FindingOverlayResponseSchema>
true satisfies AreEquivalent<FindingOverlayResponse, components['schemas']['FindingOverlayResponse']>

export const NoThreatModelProblemSchema = z
  .object({
    detail: z.string(),
    status: z.number().int(),
    title: z.string(),
  })
  .describe('NoThreatModelProblem')
export type NoThreatModelProblem = z.infer<typeof NoThreatModelProblemSchema>
true satisfies AreEquivalent<NoThreatModelProblem, components['schemas']['NoThreatModelProblem']>

export const FindingOverlaysUnavailableProblemSchema = z
  .object({
    detail: z.string(),
    status: z.number().int(),
    title: z.string(),
  })
  .describe('FindingOverlaysUnavailableProblem')
export type FindingOverlaysUnavailableProblem = z.infer<typeof FindingOverlaysUnavailableProblemSchema>
true satisfies AreEquivalent<
  FindingOverlaysUnavailableProblem,
  components['schemas']['FindingOverlaysUnavailableProblem']
>

// #endregion Finding Overlays

// #region Scorecards

export const ScorecardsStatusSchema = z.enum(['processing', 'completed', 'failed']).describe('ScorecardsStatus')
export type ScorecardsStatus = z.infer<typeof ScorecardsStatusSchema>

export const ScorecardsPhaseSchema = z
  .enum(['queued', 'fetching_code', 'loading_threat_model', 'scoring_dimensions', 'finalizing'])
  .describe('ScorecardsPhase')
export type ScorecardsPhase = z.infer<typeof ScorecardsPhaseSchema>

export const ScorecardsLinksSchema = z
  .object({
    events: HALLinkSchema.optional(),
    self: HALLinkSchema,
  })
  .describe('ScorecardsLinks')
export type ScorecardsLinks = z.infer<typeof ScorecardsLinksSchema>
true satisfies AreEquivalent<ScorecardsLinks, components['schemas']['ScorecardsLinks']>

export const DimensionAnswerEvidenceSchema = z
  .object({
    excerpt: z.string().optional(),
    location: z.string(),
    note: z.string().optional(),
  })
  .describe('DimensionAnswerEvidence')
export type DimensionAnswerEvidence = z.infer<typeof DimensionAnswerEvidenceSchema>
true satisfies AreEquivalent<DimensionAnswerEvidence, components['schemas']['DimensionAnswerEvidence']>

export const DimensionAnswerSchema = z
  .object({
    answer: z.enum(['yes', 'no', 'na']),
    evidence: DimensionAnswerEvidenceSchema.array(),
    id: z.string(),
    question: z.string(),
    rationale: z.string().optional(),
  })
  .describe('DimensionAnswer')
export type DimensionAnswer = z.infer<typeof DimensionAnswerSchema>
true satisfies AreEquivalent<DimensionAnswer, components['schemas']['DimensionAnswer']>

export const DimensionScoreSchema = z
  .object({
    answers: DimensionAnswerSchema.array(),
    confidence: z.number(),
    description: z.string(),
    dimension: z.string(),
    displayName: z.string(),
    generatedAt: InstantSchema,
    modelUsed: z.string(),
    rationale: z.string(),
    rubricVersion: z.string(),
    score: z.number(),
    status: z.enum(['failed', 'ok']),
  })
  .describe('DimensionScore')
export type DimensionScore = z.infer<typeof DimensionScoreSchema>
true satisfies AreEquivalent<DimensionScore, components['schemas']['DimensionScore']>

export const ScorecardPayloadSchema = z
  .object({
    assumptionsOverhang: z.number(),
    commitSha: z.string(),
    dimensions: DimensionScoreSchema.array(),
    generatedAt: InstantSchema,
    systemOverview: z.string(),
    threatModelCommitSha: z.string(),
    threatModelDocId: z.string(),
  })
  .describe('ScorecardPayload')
export type ScorecardPayload = z.infer<typeof ScorecardPayloadSchema>
true satisfies AreEquivalent<ScorecardPayload, components['schemas']['ScorecardPayload']>

export const ScorecardsResponseSchema = z
  .object({
    _links: ScorecardsLinksSchema,
    commitSha: z.string().optional(),
    failureReason: z.string().optional(),
    generatedAt: InstantSchema.optional(),
    phase: ScorecardsPhaseSchema,
    requestedBy: z.string().optional(),
    scorecard: ScorecardPayloadSchema.optional(),
    status: ScorecardsStatusSchema,
  })
  .describe('ScorecardsResponse')
export type ScorecardsResponse = z.infer<typeof ScorecardsResponseSchema>
true satisfies AreEquivalent<ScorecardsResponse, components['schemas']['ScorecardsResponse']>

export const ScorecardsNoThreatModelProblemSchema = z
  .object({
    detail: z.string(),
    status: z.number().int(),
    title: z.string(),
  })
  .describe('ScorecardsNoThreatModelProblem')
export type ScorecardsNoThreatModelProblem = z.infer<typeof ScorecardsNoThreatModelProblemSchema>
true satisfies AreEquivalent<ScorecardsNoThreatModelProblem, components['schemas']['ScorecardsNoThreatModelProblem']>

export const ScorecardsUnavailableProblemSchema = z
  .object({
    detail: z.string(),
    status: z.number().int(),
    title: z.string(),
  })
  .describe('ScorecardsUnavailableProblem')
export type ScorecardsUnavailableProblem = z.infer<typeof ScorecardsUnavailableProblemSchema>
true satisfies AreEquivalent<ScorecardsUnavailableProblem, components['schemas']['ScorecardsUnavailableProblem']>

// #endregion

export const PrerequisitesNotMetProblemSchema = z
  .object({
    detail: z.string(),
    needs_default_branch_scan: z.boolean(),
    needs_threat_model: z.boolean(),
    status: z.number().int(),
    title: z.string(),
  })
  .describe('PrerequisitesNotMetProblem')
export type PrerequisitesNotMetProblem = z.infer<typeof PrerequisitesNotMetProblemSchema>
true satisfies AreEquivalent<PrerequisitesNotMetProblem, components['schemas']['PrerequisitesNotMetProblem']>

export const RatingResponseSchema = z
  .object({
    description: z.string(),
    score: z.number(),
  })
  .describe('RatingResponse')
export type RatingResponse = z.infer<typeof RatingResponseSchema>
true satisfies AreEquivalent<RatingResponse, components['schemas']['RatingResponse']>

export const QualityResponseSchema = z
  .object({
    cleanliness: RatingResponseSchema,
    effectiveness: RatingResponseSchema,
    merge_readiness: RatingResponseSchema,
    safety: RatingResponseSchema,
  })
  .describe('QualityResponse')
export type QualityResponse = z.infer<typeof QualityResponseSchema>
true satisfies AreEquivalent<QualityResponse, components['schemas']['QualityResponse']>

export const ChangesetSchema = z
  .object({
    path: z.string(),
    preview: z.string(),
  })
  .describe('Changeset')
export type Changeset = z.infer<typeof ChangesetSchema>
true satisfies AreEquivalent<Changeset, components['schemas']['ChangesetResponse']>

export const ChangesetPaginatedResponseSchema = Paginated(ChangesetSchema, StandardPageNavigationLinksSchema).describe(
  'ChangesetPaginatedResponse'
)
export type ChangesetPaginatedResponse = z.infer<typeof ChangesetPaginatedResponseSchema>
true satisfies AreEquivalent<ChangesetPaginatedResponse, components['schemas']['ChangesetPaginatedResponse']>

export const AnalysisStateResponseSchema = z
  .enum(['queued', 'in-progress', 'skipped', 'completed', 'failed', 'cancelled'])
  .describe('AnalysisStateResponse')
export type AnalysisStateResponse = z.infer<typeof AnalysisStateResponseSchema>
true satisfies AreEquivalent<AnalysisStateResponse, components['schemas']['AnalysisStateResponse']>
const AnalysisStateLogResponseSchema = z
  .object({
    state: AnalysisStateResponseSchema,
    timestamp: z.string(),
  })
  .describe('AnalysisStateLogResponse')
export type AnalysisStateLogResponse = z.infer<typeof AnalysisStateLogResponseSchema>
true satisfies AreEquivalent<AnalysisStateLogResponse, components['schemas']['AnalysisStateLogResponse']>

export const AnalysisLinksSchema = z
  .object({
    cancellation: HALLinkSchema.optional(),
    preferences: HALLinkSchema.optional(),
    scan: HALLinkSchema,
    self: HALLinkSchema,
  })
  .describe('AnalysisLinks')
export type AnalysisLinks = z.infer<typeof AnalysisLinksSchema>
true satisfies AreEquivalent<AnalysisLinks, components['schemas']['AnalysisLinks']>

export const AnalysisCancellationLinksSchema = z
  .object({
    analyses: HALLinkSchema,
    scan: HALLinkSchema,
    self: HALLinkSchema,
  })
  .describe('AnalysisCancellationLinks')
export type AnalysisCancellationLinks = z.infer<typeof AnalysisCancellationLinksSchema>
true satisfies AreEquivalent<AnalysisCancellationLinks, components['schemas']['AnalysisCancellationLinks']>

export const AnalysisCancellationResponseSchema = z
  .object({
    _links: AnalysisCancellationLinksSchema,
    created_at: z.string(),
    created_by: z.string(),
    id: z.string(),
  })
  .describe('AnalysisCancellationResponse')
export type AnalysisCancellationResponse = z.infer<typeof AnalysisCancellationResponseSchema>
true satisfies AreEquivalent<AnalysisCancellationResponse, components['schemas']['AnalysisCancellationResponse']>

export const CancellationConflictProblemLinksSchema = z
  .object({
    cancellation: HALLinkSchema,
  })
  .describe('CancellationConflictProblemLinks')
export type CancellationConflictProblemLinks = z.infer<typeof CancellationConflictProblemLinksSchema>
true satisfies AreEquivalent<
  CancellationConflictProblemLinks,
  components['schemas']['CancellationConflictProblemLinks']
>

export const CancellationConflictProblemSchema = z
  .object({
    _links: CancellationConflictProblemLinksSchema,
    cancellation_id: z.string(),
    detail: z.string(),
    status: z.number().int(),
    title: z.string(),
  })
  .describe('CancellationConflictProblem')
export type CancellationConflictProblem = z.infer<typeof CancellationConflictProblemSchema>
true satisfies AreEquivalent<CancellationConflictProblem, components['schemas']['CancellationConflictProblem']>

export const FindingsSelectionSchema = z
  .object({
    finding_ids: z.array(z.string()),
    scan_id: z.string(),
  })
  .describe('FindingsSelection')
export type FindingsSelection = z.infer<typeof FindingsSelectionSchema>
true satisfies AreEquivalent<FindingsSelection, components['schemas']['FindingsSelection']>

export const AzureOrganizationSchema = z
  .object({
    name: z.string(),
  })
  .describe('AzureOrganization')
export type AzureOrganization = z.infer<typeof AzureOrganizationSchema>
true satisfies AreEquivalent<AzureOrganization, components['schemas']['AzureOrganizationResponse']>
export const AzureProjectSchema = z
  .object({
    id: z.string(),
    name: z.string(),
  })
  .describe('AzureProject')
export type AzureProject = z.infer<typeof AzureProjectSchema>
true satisfies AreEquivalent<AzureProject, components['schemas']['AzureProjectResponse']>
export const AzureIntegrationRepositorySchema = z
  .object({
    id: z.string(),
    name: z.string(),
    pixee_id: z.string().optional(),
    project: AzureProjectSchema,
  })
  .describe('AzureIntegrationRepository')
export type AzureIntegrationRepository = z.infer<typeof AzureIntegrationRepositorySchema>
true satisfies AreEquivalent<AzureIntegrationRepository, components['schemas']['IntegrationsAzureRepositoryResponse']>

export const AzureOrganizationPaginatedResponseSchema = Paginated(
  AzureOrganizationSchema,
  StandardPageNavigationLinksSchema
).describe('AzureOrganizationPaginatedResponse')
export type AzureOrganizationPaginatedResponse = z.infer<typeof AzureOrganizationPaginatedResponseSchema>
true satisfies AreEquivalent<
  AzureOrganizationPaginatedResponse,
  components['schemas']['AzureOrganizationPaginatedResponse']
>

export const AzureProjectPaginatedResponseSchema = Paginated(
  AzureProjectSchema,
  StandardPageNavigationLinksSchema
).describe('AzureProjectPaginatedResponse')
export type AzureProjectPaginatedResponse = z.infer<typeof AzureProjectPaginatedResponseSchema>
true satisfies AreEquivalent<AzureProjectPaginatedResponse, components['schemas']['AzureProjectPaginatedResponse']>

export const AzureRepositoryPaginatedResponseSchema = Paginated(
  AzureIntegrationRepositorySchema,
  StandardPageNavigationLinksSchema
).describe('AzureRepositoryPaginatedResponse')
export type AzureRepositoryPaginatedResponse = z.infer<typeof AzureRepositoryPaginatedResponseSchema>
true satisfies AreEquivalent<
  AzureRepositoryPaginatedResponse,
  components['schemas']['AzureRepositoryPaginatedResponse']
>

export const AzureAuthenticationProblemSchema = z
  .object({
    detail: z.string(),
    status: z.number().int(),
    title: z.string(),
  })
  .describe('AzureAuthenticationProblem')
export type AzureAuthenticationProblem = z.infer<typeof AzureAuthenticationProblemSchema>
true satisfies AreEquivalent<AzureAuthenticationProblem, components['schemas']['AzureAuthenticationProblem']>

// pixee-platform serializes WorkflowActionLimit via Jackson's WRAPPER_ARRAY
// mode (e.g. `["none", "INSTANCE"]`) because the OpenAPI oneOf mixes an object
// variant (FindingCountLimit) with a string variant (NoneWorkflowActionLimit),
// so Jackson cannot apply the `type` discriminator uniformly. Unwrap the
// [typeId, payload] envelope back to the spec-documented payload so the rest
// of the schema parses successfully. Remove once ISS-7309 ships and the
// backend emits the documented shape.
const unwrapWorkflowActionLimit = (value: unknown): unknown => {
  if (Array.isArray(value) && value.length === 2 && typeof value[0] === 'string') {
    return value[1]
  }
  return value
}

export const WorkflowActionSchema = z
  .discriminatedUnion('type', [
    z.object({
      findings: z
        .object({
          include_severity_labels: z.array(z.string()).optional(),
          maximum_severity_score: z.number().optional(),
          minimum_severity_score: z.number().optional(),
        })
        .optional(),
      fixes: z
        .object({
          max_count: z.number().optional(),
          minimum_fix_confidence: z.enum(['high', 'medium', 'low', 'no-rating']),
        })
        .optional(),
      limit: z
        .preprocess(
          unwrapWorkflowActionLimit,
          z.union([
            z.object({
              max: z.number(),
              type: z.literal('finding-count'),
            }),
            z.literal('INSTANCE'),
          ])
        )
        .optional(),
      type: z.literal('create-patch'),
    }),
    z.object({
      type: z.literal('none'),
    }),
  ])
  .describe('WorkflowAction')
export type WorkflowAction = z.infer<typeof WorkflowActionSchema>
// @ts-expect-error Spec uses oneOf without an explicit type discriminator; Zod uses a discriminatedUnion with one.
true satisfies AreEquivalent<WorkflowAction, components['schemas']['WorkflowAction']>

export const CreateWorkflowResponseSchema = z
  .object({
    _links: z.object({
      repository: HALLinkSchema,
      self: HALLinkSchema,
    }),
    id: z.string(),
    name: z.string(),
    type: z.string(),
  })
  .describe('CreateWorkflowResponse')
export type CreateWorkflowResponse = z.infer<typeof CreateWorkflowResponseSchema>
// @ts-expect-error Not yet in sync with spec (structural differences).
true satisfies AreEquivalent<CreateWorkflowResponse, components['schemas']['WorkflowResponse']>

export const GitLabProjectResponseSchema = z
  .object({
    name_with_namespace: z.string(),
    namespace_full_path: z.string(),
    path: z.string(),
    pixee_id: z.string().optional(),
  })
  .describe('GitLabProjectResponse')
export type GitLabProjectResponse = z.infer<typeof GitLabProjectResponseSchema>
true satisfies AreEquivalent<GitLabProjectResponse, components['schemas']['GitLabProjectResponse']>

export const GitLabProjectPaginatedResponseSchema = Paginated(
  GitLabProjectResponseSchema,
  StandardPageNavigationLinksSchema
).describe('GitLabProjectPaginatedResponse')
export type GitLabProjectPaginatedResponse = z.infer<typeof GitLabProjectPaginatedResponseSchema>
true satisfies AreEquivalent<GitLabProjectPaginatedResponse, components['schemas']['GitLabProjectPaginatedResponse']>

export const ScheduleWorkflowCadenceSchema = z.enum(['daily', 'weekly']).describe('ScheduleWorkflowCadence')
export type ScheduleWorkflowCadence = z.infer<typeof ScheduleWorkflowCadenceSchema>
true satisfies AreEquivalent<ScheduleWorkflowCadence, components['schemas']['ScheduleWorkflowCadence']>

export const ScheduleWorkflowEventSchema = z
  .object({
    branch: z.string().optional(),
    cadence: ScheduleWorkflowCadenceSchema,
    start: z.string().optional(),
    type: z.literal('schedule'),
  })
  .describe('ScheduleWorkflowEvent')
export type ScheduleWorkflowEvent = z.infer<typeof ScheduleWorkflowEventSchema>
true satisfies AreEquivalent<ScheduleWorkflowEvent, components['schemas']['ScheduleWorkflowEvent']>

export const NewScanWorkflowEventRequestSchema = z
  .object({
    branch: z.string().optional(),
    type: z.literal('new-scan'),
  })
  .describe('NewScanWorkflowEventRequest')
export type NewScanWorkflowEventRequest = z.infer<typeof NewScanWorkflowEventRequestSchema>
true satisfies AreEquivalent<NewScanWorkflowEventRequest, components['schemas']['NewScanWorkflowEventRequest']>

export const PullRequestScanWorkflowEventRequestSchema = z
  .object({
    source_branch: z.string().optional(),
    target_branch: z.string().optional(),
    type: z.literal('pull-request-scan'),
  })
  .describe('PullRequestScanWorkflowEventRequest')
export type PullRequestScanWorkflowEventRequest = z.infer<typeof PullRequestScanWorkflowEventRequestSchema>
true satisfies AreEquivalent<
  PullRequestScanWorkflowEventRequest,
  components['schemas']['PullRequestScanWorkflowEventRequest']
>

export const WorkflowEventSchema = z
  .discriminatedUnion('type', [
    ScheduleWorkflowEventSchema,
    NewScanWorkflowEventRequestSchema,
    PullRequestScanWorkflowEventRequestSchema,
  ])
  .describe('WorkflowEvent')
export type WorkflowEvent = z.infer<typeof WorkflowEventSchema>
true satisfies AreEquivalent<WorkflowEvent, components['schemas']['WorkflowEvent']>

export const WorkflowLinksSchema = z
  .object({
    repository: HALLinkSchema,
    runs: HALLinkSchema.optional(),
    self: HALLinkSchema,
  })
  .describe('WorkflowLinks')
export type WorkflowLinks = z.infer<typeof WorkflowLinksSchema>
true satisfies AreEquivalent<WorkflowLinks, components['schemas']['WorkflowLinks']>

export const WorkflowResponseSchema = z
  .object({
    _links: WorkflowLinksSchema,
    action: WorkflowActionSchema,
    created_at: z.string().optional(),
    enabled: z.boolean(),
    event: WorkflowEventSchema,
    id: z.string(),
    name: z.string(),
    tool: ToolSchema,
  })
  .describe('WorkflowResponse')
export type WorkflowResponse = z.infer<typeof WorkflowResponseSchema>
// @ts-expect-error WorkflowAction Spec uses oneOf without an explicit type discriminator; Zod uses a discriminatedUnion with one.
true satisfies AreEquivalent<WorkflowResponse, components['schemas']['WorkflowResponse']>

export const WorkflowPaginatedResponseSchema = z
  .object({
    _embedded: z.object({
      items: PermissiveDiscriminatedUnionArray(WorkflowResponseSchema),
    }),
    _links: StandardPageNavigationLinksSchema,
    page: PageResponseSchema,
    total: z.number().int(),
  })
  .describe('WorkflowPaginatedResponse')
export type WorkflowPaginatedResponse = z.infer<typeof WorkflowPaginatedResponseSchema>
// @ts-expect-error Transitive: WorkflowResponseSchema diverges from spec (repository/runs); items is permissive.
true satisfies AreEquivalent<WorkflowPaginatedResponse, components['schemas']['WorkflowPaginatedResponse']>

const CapabilitySchema = z.enum(['triage-push']).describe('Capability')
type Capability = z.infer<typeof CapabilitySchema>
true satisfies AreEquivalent<Capability, components['schemas']['Capability']>

const ToolIntegrationLinksSchema = z
  .object({
    scale: HALLinkSchema.optional(),
    self: HALLinkSchema,
  })
  .describe('ToolIntegrationLinks')
type ToolIntegrationLinks = z.infer<typeof ToolIntegrationLinksSchema>
true satisfies AreEquivalent<ToolIntegrationLinks, components['schemas']['ToolIntegrationLinks']>

const AzureIntegrationLinksSchema = z
  .object({
    organizations: HALLinkSchema.optional(),
    self: HALLinkSchema,
  })
  .describe('AzureIntegrationLinks')
type AzureIntegrationLinks = z.infer<typeof AzureIntegrationLinksSchema>
true satisfies AreEquivalent<AzureIntegrationLinks, components['schemas']['AzureIntegrationLinks']>

const BitbucketIntegrationLinksSchema = z
  .object({
    self: HALLinkSchema,
    workspaces: HALLinkSchema.optional(),
  })
  .describe('BitbucketIntegrationLinks')
type BitbucketIntegrationLinks = z.infer<typeof BitbucketIntegrationLinksSchema>
true satisfies AreEquivalent<BitbucketIntegrationLinks, components['schemas']['BitbucketIntegrationLinks']>

const CheckmarxIntegrationLinksSchema = z
  .object({
    scale: HALLinkSchema.optional(),
    self: HALLinkSchema,
  })
  .describe('CheckmarxIntegrationLinks')
type CheckmarxIntegrationLinks = z.infer<typeof CheckmarxIntegrationLinksSchema>
true satisfies AreEquivalent<CheckmarxIntegrationLinks, components['schemas']['CheckmarxIntegrationLinks']>

const GitLabIntegrationLinksSchema = z
  .object({
    projects: HALLinkSchema.optional(),
    self: HALLinkSchema,
  })
  .describe('GitLabIntegrationLinks')
type GitLabIntegrationLinks = z.infer<typeof GitLabIntegrationLinksSchema>
true satisfies AreEquivalent<GitLabIntegrationLinks, components['schemas']['GitLabIntegrationLinks']>

const VeracodeIntegrationLinksSchema = z
  .object({
    applications: HALLinkSchema.optional(),
    scale: HALLinkSchema.optional(),
    self: HALLinkSchema,
  })
  .describe('VeracodeIntegrationLinks')
type VeracodeIntegrationLinks = z.infer<typeof VeracodeIntegrationLinksSchema>
true satisfies AreEquivalent<VeracodeIntegrationLinks, components['schemas']['VeracodeIntegrationLinks']>

const IntegrationTypeSchema = z.enum([
  'sonar',
  'github',
  'gitlab',
  'azure',
  'bitbucket',
  'veracode',
  'appscan',
  'checkmarx',
  'arnica',
  'datadog',
  'polaris',
])
export type IntegrationType = z.infer<typeof IntegrationTypeSchema>
true satisfies AreEquivalent<IntegrationType, components['schemas']['IntegrationType']>
// Use a set for O(1) lookup, and type it as `unknown` to avoid the need to cast `item.type`.
const INTEGRATION_TYPE_SET = new Set<unknown>(IntegrationTypeSchema.options)

/**
 * The `Allow` header, split into methods. Internal to the client — the spec documents no OPTIONS
 * operation on the collection — so there is no `AreEquivalent` counterpart to check against.
 * A server that answers the probe with no `Allow` yields no methods, which reads as read-only.
 */
export const AllowedMethodsSchema = z
  .string()
  .nullable()
  .transform(header =>
    (header ?? '')
      .split(',')
      .map(method => method.trim().toUpperCase())
      .filter(Boolean)
  )
  .describe('AllowedMethods')

export const IntegrationHealthStatusResponseSchema = z
  .enum(['healthy', 'needs-attention', 'unknown'])
  .describe('IntegrationHealthStatusResponse')
export type IntegrationHealthStatusResponse = z.infer<typeof IntegrationHealthStatusResponseSchema>
true satisfies AreEquivalent<IntegrationHealthStatusResponse, components['schemas']['IntegrationHealthStatusResponse']>

export const IntegrationSourceResponseSchema = z.enum(['managed', 'system']).describe('IntegrationSourceResponse')
export type IntegrationSourceResponse = z.infer<typeof IntegrationSourceResponseSchema>
true satisfies AreEquivalent<IntegrationSourceResponse, components['schemas']['IntegrationSourceResponse']>

export const IntegrationHealthResponseSchema = z
  .object({
    detail: z.string().optional(),
    last_checked_at: InstantSchema.optional(),
    status: IntegrationHealthStatusResponseSchema,
  })
  .describe('IntegrationHealthResponse')
export type IntegrationHealthResponse = z.infer<typeof IntegrationHealthResponseSchema>
true satisfies AreEquivalent<IntegrationHealthResponse, components['schemas']['IntegrationHealthResponse']>

const BaseIntegrationSchema = z.object({
  capabilities: z.array(CapabilitySchema).optional(),
  health: IntegrationHealthResponseSchema.optional(),
  id: z.string().optional(),
  name: z.string(),
  source: IntegrationSourceResponseSchema,
})
const SonarIntegrationSchema = BaseIntegrationSchema.extend({
  _links: ToolIntegrationLinksSchema,
  base_uri: z.url().optional(),
  organization: z.string().optional(),
  token_configured: z.boolean().optional(),
  type: z.literal('sonar'),
  webhook_secret_configured: z.boolean().optional(),
})
export type SonarIntegration = z.infer<typeof SonarIntegrationSchema>
true satisfies AreEquivalent<SonarIntegration, components['schemas']['SonarIntegrationResponse']>
const GitHubIntegrationSchema = BaseIntegrationSchema.extend({
  _links: SelfLinkSchema,
  app_name: z.string().optional(),
  type: z.literal('github'),
})
export type GitHubIntegration = z.infer<typeof GitHubIntegrationSchema>
true satisfies AreEquivalent<GitHubIntegration, components['schemas']['GitHubIntegrationResponse']>
const GitLabIntegrationSchema = BaseIntegrationSchema.extend({
  _links: GitLabIntegrationLinksSchema,
  type: z.literal('gitlab'),
})
export type GitLabIntegration = z.infer<typeof GitLabIntegrationSchema>
true satisfies AreEquivalent<GitLabIntegration, components['schemas']['GitLabIntegrationResponse']>
const AzureIntegrationSchema = BaseIntegrationSchema.extend({
  _links: AzureIntegrationLinksSchema,
  type: z.literal('azure'),
  webhook_configured: z.boolean().optional(),
})
export type AzureIntegration = z.infer<typeof AzureIntegrationSchema>
true satisfies AreEquivalent<AzureIntegration, components['schemas']['AzureIntegrationResponse']>
const BitbucketIntegrationSchema = BaseIntegrationSchema.extend({
  _links: BitbucketIntegrationLinksSchema,
  type: z.literal('bitbucket'),
})
export type BitbucketIntegration = z.infer<typeof BitbucketIntegrationSchema>
true satisfies AreEquivalent<BitbucketIntegration, components['schemas']['BitbucketIntegrationResponse']>
export const VeracodeIntegrationSchema = BaseIntegrationSchema.extend({
  _links: VeracodeIntegrationLinksSchema,
  base_uri: z.url().optional(),
  type: z.literal('veracode'),
}).describe('VeracodeIntegration')
export type VeracodeIntegration = z.infer<typeof VeracodeIntegrationSchema>
true satisfies AreEquivalent<VeracodeIntegration, components['schemas']['VeracodeIntegrationResponse']>
const AppScanIntegrationSchema = BaseIntegrationSchema.extend({
  _links: ToolIntegrationLinksSchema,
  base_uri: z.url().optional(),
  type: z.literal('appscan'),
})
export type AppScanIntegration = z.infer<typeof AppScanIntegrationSchema>
true satisfies AreEquivalent<AppScanIntegration, components['schemas']['AppScanIntegrationResponse']>
export const CheckmarxIntegrationSchema = BaseIntegrationSchema.extend({
  _links: CheckmarxIntegrationLinksSchema,
  base_uri: z.url().optional(),
  type: z.literal('checkmarx'),
}).describe('CheckmarxIntegration')
export type CheckmarxIntegration = z.infer<typeof CheckmarxIntegrationSchema>
true satisfies AreEquivalent<CheckmarxIntegration, components['schemas']['CheckmarxIntegrationResponse']>
export const ArnicaIntegrationProvenanceSchema = z
  .enum(['pattern-matching', 'user-supplied'])
  .describe('ArnicaIntegrationProvenance')
export type ArnicaIntegrationProvenance = z.infer<typeof ArnicaIntegrationProvenanceSchema>
true satisfies AreEquivalent<ArnicaIntegrationProvenance, components['schemas']['ArnicaIntegrationProvenance']>

export const SonarIntegrationProvenanceSchema = z
  .enum(['GITHUB_CHECK_RUN', 'SONAR_WEBHOOK', 'PATTERN_MATCHING', 'USER_PROVIDED'])
  .describe('SonarIntegrationProvenance')
export type SonarIntegrationProvenance = z.infer<typeof SonarIntegrationProvenanceSchema>
true satisfies AreEquivalent<SonarIntegrationProvenance, components['schemas']['SonarIntegrationProvenance']>

const ArnicaIntegrationSchema = BaseIntegrationSchema.extend({
  _links: ToolIntegrationLinksSchema,
  base_uri: z.url().optional(),
  type: z.literal('arnica'),
})
export type ArnicaIntegration = z.infer<typeof ArnicaIntegrationSchema>
true satisfies AreEquivalent<ArnicaIntegration, components['schemas']['ArnicaIntegrationResponse']>

const DatadogIntegrationSchema = BaseIntegrationSchema.extend({
  _links: ToolIntegrationLinksSchema,
  base_uri: z.url().optional(),
  type: z.literal('datadog'),
})
export type DatadogIntegration = z.infer<typeof DatadogIntegrationSchema>
true satisfies AreEquivalent<DatadogIntegration, components['schemas']['DatadogIntegrationResponse']>

const PolarisIntegrationSchema = BaseIntegrationSchema.extend({
  _links: ToolIntegrationLinksSchema,
  base_uri: z.url().optional(),
  type: z.literal('polaris'),
})
export type PolarisIntegration = z.infer<typeof PolarisIntegrationSchema>
true satisfies AreEquivalent<PolarisIntegration, components['schemas']['PolarisIntegrationResponse']>

export const IntegrationSchema = z
  .discriminatedUnion('type', [
    SonarIntegrationSchema,
    GitHubIntegrationSchema,
    GitLabIntegrationSchema,
    AzureIntegrationSchema,
    BitbucketIntegrationSchema,
    VeracodeIntegrationSchema,
    AppScanIntegrationSchema,
    CheckmarxIntegrationSchema,
    ArnicaIntegrationSchema,
    DatadogIntegrationSchema,
    PolarisIntegrationSchema,
  ])
  .describe('Integration')
export type Integration = z.infer<typeof IntegrationSchema>
true satisfies AreEquivalent<Integration, components['schemas']['IntegrationResponseObject']>

export const IntegrationPaginatedResponseSchema = Paginated(IntegrationSchema, StandardPageNavigationLinksSchema)
  .extend({
    _embedded: z.object({
      items: z
        .array(
          z
            .object({
              type: z.unknown().optional(),
            })
            .loose()
        )
        .transform(items =>
          z.array(IntegrationSchema).parse(items.filter(item => INTEGRATION_TYPE_SET.has(item.type)))
        ),
    }),
  })
  .transform(paginatedIntegrations => ({
    ...paginatedIntegrations,
    total: paginatedIntegrations._embedded.items.length,
  }))
  .describe('IntegrationPaginatedResponse')
export type IntegrationPaginatedResponse = z.infer<typeof IntegrationPaginatedResponseSchema>
true satisfies AreEquivalent<IntegrationPaginatedResponse, components['schemas']['IntegrationPaginatedResponse']>

const CreateDatadogIntegrationRequestSchema = z
  .object({
    api_key: z.string().min(1),
    application_key: z.string().min(1),
    base_uri: z.url(),
    name: z.string().min(1),
    type: z.literal('datadog'),
  })
  .describe('CreateDatadogIntegrationRequest')
type CreateDatadogIntegrationRequest = z.infer<typeof CreateDatadogIntegrationRequestSchema>
true satisfies AreEquivalent<CreateDatadogIntegrationRequest, components['schemas']['CreateDatadogIntegrationRequest']>

export const CreateIntegrationRequestSchema = z
  .discriminatedUnion('type', [CreateDatadogIntegrationRequestSchema])
  .describe('CreateIntegrationRequest')
export type CreateIntegrationRequest = z.infer<typeof CreateIntegrationRequestSchema>
true satisfies AreEquivalent<CreateIntegrationRequest, components['schemas']['CreateIntegrationRequest']>

/**
 * The credential pair is replace-or-keep: omit both keys to keep the stored credentials, or supply
 * both to rotate them. The backend rejects one without the other, and rejects blank values.
 */
const UpdateDatadogIntegrationRequestSchema = z
  .object({
    api_key: z.string().optional(),
    application_key: z.string().optional(),
    base_uri: z.url().optional(),
    name: z.string().optional(),
    type: z.literal('datadog'),
  })
  .describe('UpdateDatadogIntegrationRequest')
type UpdateDatadogIntegrationRequest = z.infer<typeof UpdateDatadogIntegrationRequestSchema>
true satisfies AreEquivalent<UpdateDatadogIntegrationRequest, components['schemas']['UpdateDatadogIntegrationRequest']>

export const UpdateIntegrationRequestSchema = z
  .discriminatedUnion('type', [UpdateDatadogIntegrationRequestSchema])
  .describe('UpdateIntegrationRequest')
export type UpdateIntegrationRequest = z.infer<typeof UpdateIntegrationRequestSchema>
true satisfies AreEquivalent<UpdateIntegrationRequest, components['schemas']['UpdateIntegrationRequest']>

/**
 * The integration types the spec accepts a create/update body for, derived from the discriminated
 * union so that a variant shipping upstream widens the client without a second list to maintain.
 */
export type WritableIntegrationType = CreateIntegrationRequest['type']

// `getIsIntegrationTypeSupportedByApi` also gates the `UpdateIntegrationRequestSchema.parse` in
// `usePutIntegration`, which is only sound while the two unions carry the same types. A spec that
// ships a create-only variant should break the build here, not throw inside a mutation.
true satisfies AreEquivalent<CreateIntegrationRequest['type'], UpdateIntegrationRequest['type']>

const WRITABLE_INTEGRATION_TYPE_SET = new Set<string>(
  CreateIntegrationRequestSchema.options.map(option => option.shape.type.value)
)

/**
 * Whether the spec has a write contract for this type. Distinct from `getHasIntegrationSetupForm`
 * in `integration-instance-utils.ts`, which answers whether the *product* has built a setup form.
 */
export const getIsIntegrationTypeSupportedByApi = (type: string): type is WritableIntegrationType =>
  WRITABLE_INTEGRATION_TYPE_SET.has(type)

// Scan/Analysis responses live here, below IntegrationSchema, because `_embedded.integration` references it.
export const ScanResponseSchema = z
  .object({
    _embedded: z.object({ integration: IntegrationSchema.optional() }).optional(),
    _links: ScanLinksSchema,
    branch: z.string().optional(),
    detector: ToolSchema,
    expires_at: InstantSchema.optional(),
    html_url: z.string().optional(),
    id: z.string(),
    imported_at: InstantSchema,
    scanned_at: InstantSchema.optional(),
    sha: z.string(),
  })
  .describe('ScanResponse')
export type ScanResponse = z.infer<typeof ScanResponseSchema>
true satisfies AreEquivalent<ScanResponse, components['schemas']['ScanResponse']>

export const ScanAggregateResponseSchema = Paginated(ScanResponseSchema, StandardPageNavigationLinksSchema)
  .extend({
    distinct_branches: z.array(z.string()),
  })
  .describe('ScanAggregateResponse')
export type ScanAggregateResponse = z.infer<typeof ScanAggregateResponseSchema>
true satisfies AreEquivalent<ScanAggregateResponse, components['schemas']['ScanAggregateResponse']>

export const AnalysisResponseSchema = z
  .object({
    _embedded: z
      .object({
        scan: ScanResponseSchema.optional(),
      })
      .optional(),
    _links: AnalysisLinksSchema,
    current_state: AnalysisStateLogResponseSchema,
    findings_count: z.number().int().optional(),
    id: z.string(),
    log: z.array(AnalysisStateLogResponseSchema),
  })
  .describe('AnalysisResponse')
export type AnalysisResponse = z.infer<typeof AnalysisResponseSchema>
true satisfies AreEquivalent<AnalysisResponse, components['schemas']['AnalysisResponse']>

export const AnalysisPaginatedResponseSchema = Paginated(
  AnalysisResponseSchema,
  StandardPageNavigationLinksSchema
).describe('AnalysisPaginatedResponse')
export type AnalysisPaginatedResponse = z.infer<typeof AnalysisPaginatedResponseSchema>
true satisfies AreEquivalent<AnalysisPaginatedResponse, components['schemas']['AnalysisPaginatedResponse']>

const ScaleLinksSchema = z.object({
  self: HALLinkSchema,
})

export const SeveritySchema = z
  .object({
    label: z.string().optional(),
    rank: z.number(),
  })
  .describe('Severity')
export type Severity = z.infer<typeof SeveritySchema>
true satisfies AreEquivalent<Severity, components['schemas']['SeverityResponse']>

export const RankedSetResponseSchema = z
  .object({
    _links: ScaleLinksSchema,
    id: z.string(),
    severities: z.array(SeveritySchema),
    type: z.literal('ranked-set'),
  })
  .describe('RankedSetResponse')
export type RankedSetResponse = z.infer<typeof RankedSetResponseSchema>
true satisfies AreEquivalent<RankedSetResponse, components['schemas']['RankedSetResponse']>

const ScoreResponseSchema = z
  .object({
    _links: ScaleLinksSchema,
    id: z.string().optional(),
    max: z.number().optional(),
    min: z.number().optional(),
    type: z.literal('score'),
  })
  .describe('ScoreResponse')
type ScoreResponse = z.infer<typeof ScoreResponseSchema>
true satisfies AreEquivalent<ScoreResponse, components['schemas']['ScoreResponse']>

export const ScaleSchema = z
  .discriminatedUnion('type', [RankedSetResponseSchema, ScoreResponseSchema])
  .describe('Scale')
export type Scale = z.infer<typeof ScaleSchema>
true satisfies AreEquivalent<Scale, components['schemas']['ScaleResponse']>

export const ScalePaginatedResponseSchema = Paginated(ScaleSchema, StandardPageNavigationLinksSchema).describe(
  'ScalePaginatedResponse'
)
export type ScalePaginatedResponse = z.infer<typeof ScalePaginatedResponseSchema>
true satisfies AreEquivalent<ScalePaginatedResponse, components['schemas']['ScalePaginatedResponse']>

export const MergeStateResponseSchema = z
  .enum(['open', 'closed_merged', 'closed_not_merged'])
  .describe('MergeStateResponse')
export type MergeStateResponse = z.infer<typeof MergeStateResponseSchema>
true satisfies AreEquivalent<MergeStateResponse, components['schemas']['MergeStateResponse']>

export const PatchStateSchema = z
  .enum(['in-progress', 'completed-success', 'completed-failed', 'completed-fixes-overcome-by-events'])
  .describe('PatchState')
export type PatchState = z.infer<typeof PatchStateSchema>
true satisfies AreEquivalent<PatchState, components['schemas']['PatchStateResponse']>
const BasePatchSchema = z.object({
  _links: z.object({
    self: HALLinkSchema,
  }),
  id: z.string(),
  state: PatchStateSchema,
})

export const GitPatchResponseSchema = BasePatchSchema.extend({
  branch_name: z.string().optional(),
  type: z.literal('git'),
}).describe('GitPatchResponse')
export type GitPatchResponse = z.infer<typeof GitPatchResponseSchema>
true satisfies AreEquivalent<GitPatchResponse, components['schemas']['GitPatchResponse']>

const PullRequestPatchBaseSchema = BasePatchSchema.extend({
  html_url: z.string().optional(),
  merge_state: MergeStateResponseSchema.optional(),
  pr_number: z.number().optional(),
})

export const GitHubPatchResponseSchema = PullRequestPatchBaseSchema.extend({
  type: z.literal('github'),
}).describe('GitHubPatchResponse')
export type GitHubPatchResponse = z.infer<typeof GitHubPatchResponseSchema>
true satisfies AreEquivalent<GitHubPatchResponse, components['schemas']['GitHubPatchResponse']>

export const GitLabPatchResponseSchema = PullRequestPatchBaseSchema.extend({
  type: z.literal('gitlab'),
}).describe('GitLabPatchResponse')
export type GitLabPatchResponse = z.infer<typeof GitLabPatchResponseSchema>
true satisfies AreEquivalent<GitLabPatchResponse, components['schemas']['GitLabPatchResponse']>

export const AzurePatchResponseSchema = PullRequestPatchBaseSchema.extend({
  type: z.literal('azure'),
}).describe('AzurePatchResponse')
export type AzurePatchResponse = z.infer<typeof AzurePatchResponseSchema>
true satisfies AreEquivalent<AzurePatchResponse, components['schemas']['AzurePatchResponse']>

export const BitbucketPatchResponseSchema = PullRequestPatchBaseSchema.extend({
  type: z.literal('bitbucket'),
}).describe('BitbucketPatchResponse')
export type BitbucketPatchResponse = z.infer<typeof BitbucketPatchResponseSchema>
true satisfies AreEquivalent<BitbucketPatchResponse, components['schemas']['BitbucketPatchResponse']>

export const PatchSchema = z
  .discriminatedUnion('type', [
    GitPatchResponseSchema,
    GitHubPatchResponseSchema,
    GitLabPatchResponseSchema,
    AzurePatchResponseSchema,
    BitbucketPatchResponseSchema,
  ])
  .describe('Patch')
export type Patch = z.infer<typeof PatchSchema>
true satisfies AreEquivalent<Patch, components['schemas']['PatchResponse']>

export const TriageAnalyzerStrategySchema = z
  .enum(['static', 'auto_mapped', 'linter', 'dynamic'])
  .describe('TriageAnalyzerStrategy')
export type TriageAnalyzerStrategy = z.infer<typeof TriageAnalyzerStrategySchema>
true satisfies AreEquivalent<
  TriageAnalyzerStrategy,
  components['schemas']['TriageAnalyzerMetadataResponse']['strategy']
>

export const TriageAnalyzerMetadataResponseSchema = z
  .object({
    strategy: TriageAnalyzerStrategySchema,
  })
  .describe('TriageAnalyzerMetadataResponse')
export type TriageAnalyzerMetadataResponse = z.infer<typeof TriageAnalyzerMetadataResponseSchema>
true satisfies AreEquivalent<TriageAnalyzerMetadataResponse, components['schemas']['TriageAnalyzerMetadataResponse']>

export const ScaClassificationResponseSchema = z
  .enum(['exploitable', 'not-exploitable', 'inconclusive', 'potentially-exploitable'])
  .describe('ScaClassificationResponse')
export type ScaClassificationResponse = z.infer<typeof ScaClassificationResponseSchema>
true satisfies AreEquivalent<ScaClassificationResponse, components['schemas']['ScaClassificationResponse']>

export const DuplicateRepositoryIntegrationProblemResponseSchema = z
  .object({
    detail: z.string(),
    status: z.number(),
    title: z.string(),
  })
  .describe('DuplicateRepositoryIntegrationProblem')
export type DuplicateRepositoryIntegrationProblemResponse = z.infer<
  typeof DuplicateRepositoryIntegrationProblemResponseSchema
>
true satisfies AreEquivalent<
  DuplicateRepositoryIntegrationProblemResponse,
  components['schemas']['DuplicateRepositoryIntegrationProblem']
>

export const UnknownIntegrationProblemResponseSchema = z
  .object({
    detail: z.string(),
    integration_id: z.string(),
    status: z.number().int(),
    title: z.string(),
  })
  .describe('UnknownIntegrationProblem')
export type UnknownIntegrationProblemResponse = z.infer<typeof UnknownIntegrationProblemResponseSchema>
true satisfies AreEquivalent<UnknownIntegrationProblemResponse, components['schemas']['UnknownIntegrationProblem']>

export const IntegrationTypeMismatchProblemResponseSchema = z
  .object({
    detail: z.string(),
    integration_id: z.string(),
    status: z.number().int(),
    title: z.string(),
  })
  .describe('IntegrationTypeMismatchProblem')
export type IntegrationTypeMismatchProblemResponse = z.infer<typeof IntegrationTypeMismatchProblemResponseSchema>
true satisfies AreEquivalent<
  IntegrationTypeMismatchProblemResponse,
  components['schemas']['IntegrationTypeMismatchProblem']
>

export const ScanNotAnalyzableProblemResponseSchema = z
  .object({
    detail: z.string(),
    status: z.number(),
    title: z.string(),
    type: z.literal('pixee:problems/api/analysis/scan-not-analyzable'),
  })
  .describe('ScanNotAnalyzableProblem')
export type ScanNotAnalyzableProblemResponse = z.infer<typeof ScanNotAnalyzableProblemResponseSchema>
true satisfies AreEquivalent<ScanNotAnalyzableProblemResponse, components['schemas']['ScanNotAnalyzableProblem']>

export const FindingsNotFoundProblemResponseSchema = z
  .object({
    detail: z.string(),
    missing_finding_ids: z.array(z.string()),
    status: z.number(),
    title: z.string(),
    type: z.literal('pixee:problems/api/analysis/findings-not-found'),
  })
  .describe('FindingsNotFoundProblem')
export type FindingsNotFoundProblemResponse = z.infer<typeof FindingsNotFoundProblemResponseSchema>
true satisfies AreEquivalent<FindingsNotFoundProblemResponse, components['schemas']['FindingsNotFoundProblem']>

export const AnalyzeScanProblemResponseSchema = z
  .union([FindingsNotFoundProblemResponseSchema, ScanNotAnalyzableProblemResponseSchema])
  .describe('AnalyzeScanProblem')
export type AnalyzeScanProblemResponse = z.infer<typeof AnalyzeScanProblemResponseSchema>
true satisfies AreEquivalent<AnalyzeScanProblemResponse, components['schemas']['AnalyzeScanProblem']>

export const TriageSuggestedStatusResponseSchema = z
  .enum([
    'wont_fix',
    'false_positive',
    'true_positive',
    'suspicious',
    'inconclusive',
    'not_triaged',
    'blocked',
    'excluded',
    'error',
  ])
  .describe('TriageSuggestedStatusResponse')
export type TriageSuggestedStatusResponse = z.infer<typeof TriageSuggestedStatusResponseSchema>
true satisfies AreEquivalent<TriageSuggestedStatusResponse, components['schemas']['TriageSuggestedStatusResponse']>

export const SeverityUpdateFilterSchema = z
  .enum(['increased', 'decreased', 'no_update'])
  .describe('SeverityUpdateFilter')
export type SeverityUpdateFilter = z.infer<typeof SeverityUpdateFilterSchema>
true satisfies AreEquivalent<SeverityUpdateFilter, components['schemas']['SeverityUpdateFilter']>

export const LocationSchema = z
  .object({
    file: z.string(),
    line: z.number(),
    snippet: z.string().optional(),
  })
  .describe('Location')
export type Location = z.infer<typeof LocationSchema>
true satisfies AreEquivalent<Location, components['schemas']['Location']>

export const EvidenceSchema = z
  .object({
    description: z.string(),
    locations: z.array(LocationSchema).optional(),
    type: z.string(),
  })
  .describe('Evidence')
export type Evidence = z.infer<typeof EvidenceSchema>
true satisfies AreEquivalent<Evidence, components['schemas']['Evidence']>

export const StrategyResponseSchema = z
  .enum(['provisional_ai', 'ai', 'deterministic', 'hybrid'])
  .describe('StrategyResponse')
export type StrategyResponse = z.infer<typeof StrategyResponseSchema>
true satisfies AreEquivalent<StrategyResponse, components['schemas']['StrategyResponse']>

export const ConfidenceSchema = z.enum(['high', 'medium', 'low', 'no-rating']).describe('Confidence')
export type Confidence = z.infer<typeof ConfidenceSchema>
true satisfies AreEquivalent<Confidence, components['schemas']['Confidence']>

export const CompletedTriageOutcomeSchema = z
  .object({
    analyzer_metadata: TriageAnalyzerMetadataResponseSchema.optional(),
    evidence: z.array(EvidenceSchema).optional(),
    suggested_severity: SeveritySchema.optional(),
    suggested_status: TriageSuggestedStatusResponseSchema.optional(),
    summary: z.string(),
    type: z.literal('completed-triage'),
  })
  .describe('CompletedTriageOutcome')
export type CompletedTriageOutcome = z.infer<typeof CompletedTriageOutcomeSchema>
true satisfies AreEquivalent<CompletedTriageOutcome, components['schemas']['CompletedTriageOutcome']>

export const FailedTriageOutcomeSchema = z
  .object({
    problem: z.object({
      code: z.string(),
      message: z.string(),
    }),
    type: z.literal('failed-triage'),
  })
  .describe('FailedTriageOutcome')
export type FailedTriageOutcome = z.infer<typeof FailedTriageOutcomeSchema>
true satisfies AreEquivalent<FailedTriageOutcome, components['schemas']['FailedTriageOutcome']>

export const NoRecommendationsTriageOutcomeSchema = z
  .object({
    type: z.literal('no-recommendations-triage'),
  })
  .describe('NoRecommendationsTriageOutcome')
export type NoRecommendationsTriageOutcome = z.infer<typeof NoRecommendationsTriageOutcomeSchema>
true satisfies AreEquivalent<NoRecommendationsTriageOutcome, components['schemas']['NoRecommendationsTriageOutcome']>

export const BlockedTriageOutcomeSchema = z
  .object({
    details: z.string().optional(),
    reason: z.string().optional(),
    type: z.literal('blocked-triage'),
  })
  .describe('BlockedTriageOutcome')
export type BlockedTriageOutcome = z.infer<typeof BlockedTriageOutcomeSchema>
true satisfies AreEquivalent<BlockedTriageOutcome, components['schemas']['BlockedTriageOutcome']>

export const ExcludedTriageOutcomeSchema = z
  .object({
    details: z.string().optional(),
    reason: z.string().optional(),
    type: z.literal('excluded-triage'),
  })
  .describe('ExcludedTriageOutcome')
export type ExcludedTriageOutcome = z.infer<typeof ExcludedTriageOutcomeSchema>
true satisfies AreEquivalent<ExcludedTriageOutcome, components['schemas']['ExcludedTriageOutcome']>

export const CancelledTriageOutcomeSchema = z
  .object({
    type: z.literal('cancelled-triage'),
  })
  .describe('CancelledTriageOutcome')
export type CancelledTriageOutcome = z.infer<typeof CancelledTriageOutcomeSchema>
true satisfies AreEquivalent<CancelledTriageOutcome, components['schemas']['CancelledTriageOutcome']>

export const TriageOutcomeSchema = z
  .discriminatedUnion('type', [
    CompletedTriageOutcomeSchema,
    FailedTriageOutcomeSchema,
    NoRecommendationsTriageOutcomeSchema,
    BlockedTriageOutcomeSchema,
    ExcludedTriageOutcomeSchema,
    CancelledTriageOutcomeSchema,
  ])
  .describe('TriageOutcome')
export type TriageOutcome = z.infer<typeof TriageOutcomeSchema>
true satisfies AreEquivalent<TriageOutcome, components['schemas']['TriageOutcome']>

export const CompletedFixOutcomeSchema = z
  .object({
    confidence: ConfidenceSchema.optional(),
    description: z.string().optional(),
    quality: QualityResponseSchema.optional(),
    strategy: StrategyResponseSchema,
    summary: z.string().optional(),
    type: z.literal('completed-fix'),
  })
  .describe('CompletedFixOutcome')
export type CompletedFixOutcome = z.infer<typeof CompletedFixOutcomeSchema>
true satisfies AreEquivalent<CompletedFixOutcome, components['schemas']['CompletedFixOutcome']>

export const FailedFixOutcomeSchema = z
  .object({
    problem: z.object({
      code: z.string(),
      message: z.string(),
    }),
    type: z.literal('failed-fix'),
  })
  .describe('FailedFixOutcome')
export type FailedFixOutcome = z.infer<typeof FailedFixOutcomeSchema>
true satisfies AreEquivalent<FailedFixOutcome, components['schemas']['FailedFixOutcome']>

export const NoRecommendationsFixOutcomeSchema = z
  .object({
    type: z.literal('no-recommendations-fix'),
  })
  .describe('NoRecommendationsFixOutcome')
export type NoRecommendationsFixOutcome = z.infer<typeof NoRecommendationsFixOutcomeSchema>
true satisfies AreEquivalent<NoRecommendationsFixOutcome, components['schemas']['NoRecommendationsFixOutcome']>

export const BlockedFixOutcomeSchema = z
  .object({
    details: z.string().optional(),
    reason: z.string().optional(),
    type: z.literal('blocked-fix'),
  })
  .describe('BlockedFixOutcome')
export type BlockedFixOutcome = z.infer<typeof BlockedFixOutcomeSchema>
true satisfies AreEquivalent<BlockedFixOutcome, components['schemas']['BlockedFixOutcome']>

// out-of-scope-fix was removed from the OpenAPI spec (use excluded-fix going forward),
// but kept here for backward compatibility with existing backend data
export const OutOfScopeFixOutcomeSchema = z
  .object({
    details: z.string().optional(),
    reason: z.string().optional(),
    type: z.literal('out-of-scope-fix'),
  })
  .describe('OutOfScopeFixOutcome')
export type OutOfScopeFixOutcome = z.infer<typeof OutOfScopeFixOutcomeSchema>

export const ExcludedFixOutcomeSchema = z
  .object({
    details: z.string().optional(),
    reason: z.string().optional(),
    type: z.literal('excluded-fix'),
  })
  .describe('ExcludedFixOutcome')
export type ExcludedFixOutcome = z.infer<typeof ExcludedFixOutcomeSchema>
true satisfies AreEquivalent<ExcludedFixOutcome, components['schemas']['ExcludedFixOutcome']>

export const CancelledFixOutcomeSchema = z
  .object({
    type: z.literal('cancelled-fix'),
  })
  .describe('CancelledFixOutcome')
export type CancelledFixOutcome = z.infer<typeof CancelledFixOutcomeSchema>
true satisfies AreEquivalent<CancelledFixOutcome, components['schemas']['CancelledFixOutcome']>

export const FixOutcomeSchema = z
  .discriminatedUnion('type', [
    CompletedFixOutcomeSchema,
    FailedFixOutcomeSchema,
    NoRecommendationsFixOutcomeSchema,
    BlockedFixOutcomeSchema,
    ExcludedFixOutcomeSchema,
    CancelledFixOutcomeSchema,
  ])
  .describe('FixOutcome')
export type FixOutcome = z.infer<typeof FixOutcomeSchema>
true satisfies AreEquivalent<FixOutcome, components['schemas']['FixOutcome']>

export const FindingResultLinksSchema = z
  .object({
    analysis: HALLinkSchema,
    finding: HALLinkSchema,
    self: HALLinkSchema,
  })
  .describe('FindingResultLinks')
export type FindingResultLinks = z.infer<typeof FindingResultLinksSchema>
true satisfies AreEquivalent<FindingResultLinks, components['schemas']['FindingResultLinks']>

const FindingResultResponseBaseSchema = z.object({
  _links: FindingResultLinksSchema,
  completed: InstantSchema.optional(),
  type: z.enum(['fix', 'triage', 'sca']),
})

export const TriageResultLinksSchema = z
  .object({
    analysis: HALLinkSchema,
    article: HALLinkSchema.optional(),
    'feedback-submissions': HALLinkSchema.optional(),
    finding: HALLinkSchema,
    report: HALLinkSchema.optional(),
    self: HALLinkSchema,
    verification: HALLinkSchema.optional(),
  })
  .describe('TriageResultLinks')
export type TriageResultLinks = z.infer<typeof TriageResultLinksSchema>
true satisfies AreEquivalent<TriageResultLinks, components['schemas']['TriageResultLinks']>

// #region Push schemas

export const PushStatusSchema = z.enum(['IN_PROGRESS', 'SUCCESS', 'FAILED'])
export type PushStatus = z.infer<typeof PushStatusSchema>
true satisfies AreEquivalent<PushStatus, components['schemas']['Status']>

export const PushResponseSchema = z.object({
  created_at: InstantSchema,
  error_reason: z.string().optional(),
  id: z.string(),
  status: PushStatusSchema,
  triage_result_ids: z.array(z.string()),
  updated_at: InstantSchema,
})
export type PushResponse = z.infer<typeof PushResponseSchema>
true satisfies AreEquivalent<PushResponse, components['schemas']['PushesPushResponse']>

/**
 * Named `ReasonCode` in the spec; renamed here so it reads as the reason a `SkippedEntry` carries.
 *
 * Declaration order is user-visible: `polaris-push-confirmation-dialog.tsx` orders its paragraphs by
 * `.options`, and the spec declares these in the reverse order, so an OpenAPI re-sync that regenerates
 * this enum in spec order would silently reorder the dialog. Keep this order, or give the dialog an
 * explicit display order.
 */
export const SkippedEntryReasonSchema = z
  .enum(['scan-disconnected', 'no-correlation-id', 'no-completed-outcome'])
  .describe('SkippedEntryReason')
export type SkippedEntryReason = z.infer<typeof SkippedEntryReasonSchema>
true satisfies AreEquivalent<SkippedEntryReason, components['schemas']['ReasonCode']>

const SkippedEntrySchema = z.object({
  reason: SkippedEntryReasonSchema,
  triage_result_id: z.string(),
})
export type SkippedEntry = z.infer<typeof SkippedEntrySchema>
true satisfies AreEquivalent<SkippedEntry, components['schemas']['SkippedEntryResponse']>

/** Declaration order is user-visible in the confirmation dialog. @see SkippedEntryReasonSchema */
export const PolarisStatusStateSchema = z
  .enum(['match', 'conflict', 'not-found', 'lookup-failed', 'not-applicable'])
  .describe('PolarisStatusState')
export type PolarisStatusState = z.infer<typeof PolarisStatusStateSchema>
true satisfies AreEquivalent<PolarisStatusState, components['schemas']['PushesPolarisStatusState']>

export const PolarisStatusResponseSchema = z
  .object({
    acknowledged_at: InstantSchema.optional(),
    acknowledged_by: z.string().optional(),
    comment: z.string().optional(),
    current_status: z.string().optional(),
    error_reason: z.string().optional(),
    state: PolarisStatusStateSchema,
    triage_result_id: z.string(),
  })
  .describe('PolarisStatusResponse')
export type PolarisStatusResponse = z.infer<typeof PolarisStatusResponseSchema>
true satisfies AreEquivalent<PolarisStatusResponse, components['schemas']['PushesPolarisStatusResponse']>

export const PolarisStatusLookupResponseSchema = z
  .object({
    skipped: z.array(SkippedEntrySchema),
    statuses: z.array(PolarisStatusResponseSchema),
  })
  .describe('PolarisStatusLookupResponse')
export type PolarisStatusLookupResponse = z.infer<typeof PolarisStatusLookupResponseSchema>
true satisfies AreEquivalent<PolarisStatusLookupResponse, components['schemas']['PushesPolarisStatusLookupResponse']>

export const CreatePushRequestSchema = z.object({
  scan_id: z.string().optional(),
  triage_result_ids: z.array(z.string()).optional(),
})
export type CreatePushRequest = z.infer<typeof CreatePushRequestSchema>
true satisfies AreEquivalent<CreatePushRequest, components['schemas']['CreatePushRequest']>

export const CreatePushResponseSchema = z.object({
  pushes: z.array(PushResponseSchema),
  skipped: z.array(SkippedEntrySchema),
})
export type CreatePushResponse = z.infer<typeof CreatePushResponseSchema>
true satisfies AreEquivalent<CreatePushResponse, components['schemas']['PushesCreatePushResponse']>

export const GetPushesResponseSchema = z.object({
  pushes: z.array(PushResponseSchema),
})
export type GetPushesResponse = z.infer<typeof GetPushesResponseSchema>
true satisfies AreEquivalent<GetPushesResponse, components['schemas']['PushesGetPushesResponse']>

// #endregion

export const ResultsTriageResultResponseSchema = FindingResultResponseBaseSchema.extend({
  _links: TriageResultLinksSchema,
  id: z.string(),
  latest_push_state: PushResponseSchema.optional(),
  outcome: TriageOutcomeSchema.optional(),
  type: z.literal('triage'),
}).describe('ResultsTriageResultResponse')
export type ResultsTriageResultResponse = z.infer<typeof ResultsTriageResultResponseSchema>
true satisfies AreEquivalent<ResultsTriageResultResponse, components['schemas']['ResultsTriageResultResponse']>

export const FixResultLinksSchema = z
  .object({
    analysis: HALLinkSchema,
    changesets: HALLinkSchema,
    'feedback-submissions': HALLinkSchema.optional(),
    finding: HALLinkSchema,
    'latest-patch': HALLinkSchema,
    patches: HALLinkSchema,
    self: HALLinkSchema,
  })
  .describe('FixResultLinks')
export type FixResultLinks = z.infer<typeof FixResultLinksSchema>
true satisfies AreEquivalent<FixResultLinks, components['schemas']['FixResultLinks']>

export const PatchPaginatedResponseSchema = Paginated(PatchSchema, StandardPageNavigationLinksSchema).describe(
  'PatchPaginatedResponse'
)
export type PatchPaginatedResponse = z.infer<typeof PatchPaginatedResponseSchema>
true satisfies AreEquivalent<PatchPaginatedResponse, components['schemas']['PatchPaginatedResponse']>

export const FixResultEmbeddedSchema = z
  .object({
    'latest-patch': PatchPaginatedResponseSchema.optional(),
  })
  .describe('FixResultEmbedded')
export type FixResultEmbedded = z.infer<typeof FixResultEmbeddedSchema>
true satisfies AreEquivalent<FixResultEmbedded, components['schemas']['FixResultEmbedded']>

export const FixResultResponseSchema = FindingResultResponseBaseSchema.extend({
  _embedded: FixResultEmbeddedSchema.optional(),
  _links: FixResultLinksSchema,
  id: z.string(),
  outcome: FixOutcomeSchema.optional(),
  type: z.literal('fix'),
}).describe('FixResultResponse')
export type FixResultResponse = z.infer<typeof FixResultResponseSchema>
true satisfies AreEquivalent<FixResultResponse, components['schemas']['FixResultResponse']>

export const ScaResultLinksSchema = z
  .object({
    analysis: HALLinkSchema,
    article: HALLinkSchema.optional(),
    'feedback-submissions': HALLinkSchema.optional(),
    finding: HALLinkSchema,
    report: HALLinkSchema.optional(),
    self: HALLinkSchema,
  })
  .describe('ScaResultLinks')
export type ScaResultLinks = z.infer<typeof ScaResultLinksSchema>
true satisfies AreEquivalent<ScaResultLinks, components['schemas']['ScaResultLinks']>

export const CompletedScaOutcomeSchema = z
  .object({
    classification: ScaClassificationResponseSchema,
    summary: z.string().optional(),
    type: z.literal('completed-sca'),
  })
  .describe('CompletedScaOutcome')
export type CompletedScaOutcome = z.infer<typeof CompletedScaOutcomeSchema>
true satisfies AreEquivalent<CompletedScaOutcome, components['schemas']['CompletedScaOutcome']>

export const FailedScaProblemDetailsSchema = z
  .object({
    detail: z.string(),
    title: z.string(),
  })
  .describe('FailedScaProblemDetails')
export type FailedScaProblemDetails = z.infer<typeof FailedScaProblemDetailsSchema>
true satisfies AreEquivalent<FailedScaProblemDetails, components['schemas']['FailedScaProblemDetails']>

export const FailedScaOutcomeSchema = z
  .object({
    problem: FailedScaProblemDetailsSchema,
    type: z.literal('failed-sca'),
  })
  .describe('FailedScaOutcome')
export type FailedScaOutcome = z.infer<typeof FailedScaOutcomeSchema>
true satisfies AreEquivalent<FailedScaOutcome, components['schemas']['FailedScaOutcome']>

export const BlockedScaOutcomeSchema = z
  .object({
    details: z.string().optional(),
    reason: z.string().optional(),
    type: z.literal('blocked-sca'),
  })
  .describe('BlockedScaOutcome')
export type BlockedScaOutcome = z.infer<typeof BlockedScaOutcomeSchema>
true satisfies AreEquivalent<BlockedScaOutcome, components['schemas']['BlockedScaOutcome']>

export const ExcludedScaOutcomeSchema = z
  .object({
    details: z.string().optional(),
    reason: z.string().optional(),
    type: z.literal('excluded-sca'),
  })
  .describe('ExcludedScaOutcome')
export type ExcludedScaOutcome = z.infer<typeof ExcludedScaOutcomeSchema>
true satisfies AreEquivalent<ExcludedScaOutcome, components['schemas']['ExcludedScaOutcome']>

export const CancelledScaOutcomeSchema = z
  .object({
    type: z.literal('cancelled-sca'),
  })
  .describe('CancelledScaOutcome')
export type CancelledScaOutcome = z.infer<typeof CancelledScaOutcomeSchema>
true satisfies AreEquivalent<CancelledScaOutcome, components['schemas']['CancelledScaOutcome']>

export const ScaOutcomeSchema = z
  .discriminatedUnion('type', [
    CompletedScaOutcomeSchema,
    FailedScaOutcomeSchema,
    BlockedScaOutcomeSchema,
    ExcludedScaOutcomeSchema,
    CancelledScaOutcomeSchema,
  ])
  .describe('ScaOutcome')
export type ScaOutcome = z.infer<typeof ScaOutcomeSchema>
true satisfies AreEquivalent<ScaOutcome, components['schemas']['ScaOutcome']>

export const ResultsScaResultResponseSchema = FindingResultResponseBaseSchema.extend({
  _links: ScaResultLinksSchema,
  id: z.string(),
  outcome: ScaOutcomeSchema.optional(),
  type: z.literal('sca'),
}).describe('ResultsScaResultResponse')
export type ResultsScaResultResponse = z.infer<typeof ResultsScaResultResponseSchema>
true satisfies AreEquivalent<ResultsScaResultResponse, components['schemas']['ResultsScaResultResponse']>

export const FindingResultResponseSchema = z
  .discriminatedUnion('type', [
    FixResultResponseSchema,
    ResultsTriageResultResponseSchema,
    ResultsScaResultResponseSchema,
  ])
  .describe('FindingResultResponse')
export type FindingResultResponse = z.infer<typeof FindingResultResponseSchema>
true satisfies AreEquivalent<FindingResultResponse, components['schemas']['FindingResultResponse']>

export const TriageStatusCountsAggregateSchema = z
  .object({
    completed: z.number().int(),
    failed: z.number().int(),
    in_progress: z.number().int(),
    no_recommendations: z.number().int(),
  })
  .describe('TriageStatusCountsAggregate')
export type TriageStatusCountsAggregate = z.infer<typeof TriageStatusCountsAggregateSchema>
true satisfies AreEquivalent<TriageStatusCountsAggregate, components['schemas']['ScansTriageStatusCounts']>

export const TriageOutcomeCountsSchema = z
  .object({
    blocked: z.number().int(),
    excluded: z.number().int(),
    false_positive: z.number().int(),
    inconclusive: z.number().int(),
    likely_true_positive: z.number().int(),
    not_triaged: z.number().int(),
    true_positive: z.number().int(),
    wont_fix: z.number().int(),
  })
  .describe('TriageOutcomeCounts')
export type TriageOutcomeCounts = z.infer<typeof TriageOutcomeCountsSchema>
true satisfies AreEquivalent<TriageOutcomeCounts, components['schemas']['TriageOutcomeCounts']>

export const TriageCountsSchema = z
  .object({
    outcome: TriageOutcomeCountsSchema,
    status: TriageStatusCountsAggregateSchema,
  })
  .describe('TriageCounts')
export type TriageCounts = z.infer<typeof TriageCountsSchema>
true satisfies AreEquivalent<TriageCounts, components['schemas']['TriageCounts']>

export const FixStatusCountsSchema = z
  .object({
    completed: z.number().int(),
    failed: z.number().int(),
    in_progress: z.number().int(),
    no_recommendations: z.number().int(),
  })
  .describe('FixStatusCounts')
export type FixStatusCounts = z.infer<typeof FixStatusCountsSchema>
true satisfies AreEquivalent<FixStatusCounts, components['schemas']['FixStatusCounts']>

export const FixOutcomeCountsSchema = z
  .object({
    blocked: z.number().int(),
    excluded: z.number().int(),
    has_fix: z.number().int(),
  })
  .describe('FixOutcomeCounts')
export type FixOutcomeCounts = z.infer<typeof FixOutcomeCountsSchema>
true satisfies AreEquivalent<FixOutcomeCounts, components['schemas']['FixOutcomeCounts']>

export const FixConfidenceCountsAggregateSchema = z
  .object({
    high: z.number().int(),
    low: z.number().int(),
    medium: z.number().int(),
    no_rating: z.number().int(),
  })
  .describe('FixConfidenceCountsAggregate')
export type FixConfidenceCountsAggregate = z.infer<typeof FixConfidenceCountsAggregateSchema>
true satisfies AreEquivalent<FixConfidenceCountsAggregate, components['schemas']['ScansFixConfidenceCounts']>

export const FixCountsSchema = z
  .object({
    confidence: FixConfidenceCountsAggregateSchema,
    outcome: FixOutcomeCountsSchema,
    status: FixStatusCountsSchema,
  })
  .describe('FixCounts')
export type FixCounts = z.infer<typeof FixCountsSchema>
true satisfies AreEquivalent<FixCounts, components['schemas']['FixCounts']>

export const ScaStatusCountsSchema = z
  .object({
    blocked: z.number().int(),
    completed: z.number().int(),
    excluded: z.number().int(),
    failed: z.number().int(),
    in_progress: z.number().int(),
  })
  .describe('ScaStatusCounts')
export type ScaStatusCounts = z.infer<typeof ScaStatusCountsSchema>
true satisfies AreEquivalent<ScaStatusCounts, components['schemas']['ScaStatusCounts']>

export const ScaStatusResponseSchema = z
  .enum(['completed', 'failed', 'blocked', 'excluded', 'not-analyzed'])
  .describe('ScaStatusResponse')
export type ScaStatusResponse = z.infer<typeof ScaStatusResponseSchema>
true satisfies AreEquivalent<ScaStatusResponse, components['schemas']['ScaStatusResponse']>

export const FindingEmbedParameterSchema = z
  .enum(['items.results.latest-patch', 'items.representative-results.latest-patch'])
  .describe('FindingEmbedParameter')
export type FindingEmbedParameter = z.infer<typeof FindingEmbedParameterSchema>
true satisfies AreEquivalent<FindingEmbedParameter, components['schemas']['FindingEmbedParameter']>

export const ScaClassificationCountsAggregateSchema = z
  .object({
    exploitable: z.number().int(),
    inconclusive: z.number().int(),
    not_exploitable: z.number().int(),
  })
  .describe('ScaClassificationCountsAggregate')
export type ScaClassificationCountsAggregate = z.infer<typeof ScaClassificationCountsAggregateSchema>
true satisfies AreEquivalent<ScaClassificationCountsAggregate, components['schemas']['ScansScaClassificationCounts']>

export const ScaCountsSchema = z
  .object({
    classification: ScaClassificationCountsAggregateSchema,
    status: ScaStatusCountsSchema,
  })
  .describe('ScaCounts')
export type ScaCounts = z.infer<typeof ScaCountsSchema>
true satisfies AreEquivalent<ScaCounts, components['schemas']['ScaCounts']>

export const CompositeCountsSchema = z
  .object({
    no_fix_necessary: z.number().int(),
    ready_to_fix: z.number().int(),
  })
  .describe('CompositeCounts')
export type CompositeCounts = z.infer<typeof CompositeCountsSchema>
true satisfies AreEquivalent<CompositeCounts, components['schemas']['CompositeCounts']>

export const ScansFindingResultsCollectionLinksSchema = z
  .object({
    finding: HALLinkSchema,
    first: HALLinkSchema,
    next: HALLinkSchema.optional(),
    prev: HALLinkSchema.optional(),
    self: HALLinkSchema,
  })
  .describe('ScansFindingResultsCollectionLinks')
export type ScansFindingResultsCollectionLinks = z.infer<typeof ScansFindingResultsCollectionLinksSchema>
true satisfies AreEquivalent<
  ScansFindingResultsCollectionLinks,
  components['schemas']['ScansFindingResultsCollectionLinks']
>

export const FindingResultPaginatedResponseSchema = Paginated(
  FindingResultResponseSchema,
  ScansFindingResultsCollectionLinksSchema
).describe('FindingResultPaginatedResponse')
export type FindingResultPaginatedResponse = z.infer<typeof FindingResultPaginatedResponseSchema>
true satisfies AreEquivalent<FindingResultPaginatedResponse, components['schemas']['FindingResultPaginatedResponse']>

export const FindingEmbeddedSchema = z
  .object({
    'representative-results': FindingResultPaginatedResponseSchema.optional(),
    results: FindingResultPaginatedResponseSchema.optional(),
  })
  .describe('FindingEmbedded')
export type FindingEmbedded = z.infer<typeof FindingEmbeddedSchema>
true satisfies AreEquivalent<FindingEmbedded, components['schemas']['FindingEmbedded']>

export const FindingLinksSchema = z
  .object({
    'representative-results': HALLinkSchema,
    results: HALLinkSchema,
    self: HALLinkSchema,
  })
  .describe('FindingLinks')
export type FindingLinks = z.infer<typeof FindingLinksSchema>
true satisfies AreEquivalent<FindingLinks, components['schemas']['FindingLinks']>

export const FindingResponseSchema = z
  .object({
    _embedded: FindingEmbeddedSchema.optional(),
    _links: FindingLinksSchema,
    html_url: z.string().optional(),
    id: z.string(),
    push_state: PushResponseSchema.nullish(),
    rule: z.string(),
    severity: SeveritySchema.optional(),
    title: z.string().optional(),
  })
  .describe('FindingResponse')
export type FindingResponse = z.infer<typeof FindingResponseSchema>
// @ts-expect-error OpenAPI YAML marks `push_state` nullable (`type: 'null'` alongside the $ref), but openapi-typescript's generated .d.ts drops the null variant for $ref'd fields, so `components['schemas']['FindingResponse']['push_state']` only shows the non-null type.
true satisfies AreEquivalent<FindingResponse, components['schemas']['FindingResponse']>

// TODO(ISS-5664): Remove once the API spec has a dedicated schema without _embedded https://app.devrev.ai/pixee/works/ISS-5664
export const FindingResponseWithoutEmbedSchema = FindingResponseSchema.omit({ _embedded: true }).describe(
  'FindingResponseWithoutEmbed'
)
export type FindingResponseWithoutEmbed = z.infer<typeof FindingResponseWithoutEmbedSchema>

export const FindingAggregateResponseSchema = Paginated(FindingResponseSchema, StandardPageNavigationLinksSchema)
  .extend({
    completed_analysis: z.number().int(),
    composite: CompositeCountsSchema,
    fix: FixCountsSchema,
    in_progress_analysis: z.number().int(),
    sca: ScaCountsSchema,
    total: z.number().int(),
    triage: TriageCountsSchema,
  })
  .describe('FindingAggregateResponse')
export type FindingAggregateResponse = z.infer<typeof FindingAggregateResponseSchema>
// @ts-expect-error Not yet in sync with spec (camelCase vs snake_case fields).
true satisfies AreEquivalent<FindingAggregateResponse, components['schemas']['FindingAggregateResponse']>

export const FindingAggregateResponseStatisticsSchema = FindingAggregateResponseSchema.pick({
  completed_analysis: true,
  composite: true,
  fix: true,
  in_progress_analysis: true,
  sca: true,
  total: true,
  triage: true,
}).describe('FindingAggregateResponseStatistics')
export type FindingAggregateResponseStatistics = z.infer<typeof FindingAggregateResponseStatisticsSchema>

export const RepositoryIntegrationsLinkSchema = z
  .object({
    integration: HALLinkSchema.optional(),
    self: HALLinkSchema,
  })
  .describe('RepositoryIntegrationsLink')
export type RepositoryIntegrationsLink = z.infer<typeof RepositoryIntegrationsLinkSchema>
true satisfies AreEquivalent<RepositoryIntegrationsLink, components['schemas']['RepositoryIntegrationsLink']>

const RepositoryIntegrationResponseBaseSchema = z.object({
  _links: RepositoryIntegrationsLinkSchema,
  id: z.string(),
})

export const CheckmarxRepositoryIntegrationSchema = RepositoryIntegrationResponseBaseSchema.extend({
  html_url: z.url(),
  project_id: z.string(),
  type: z.literal('checkmarx'),
}).describe('CheckmarxRepositoryIntegration')
export type CheckmarxRepositoryIntegration = z.infer<typeof CheckmarxRepositoryIntegrationSchema>
true satisfies AreEquivalent<
  CheckmarxRepositoryIntegration,
  components['schemas']['CheckmarxRepositoryIntegrationResponse']
>

export const SonarRepositoryIntegrationSchema = RepositoryIntegrationResponseBaseSchema.extend({
  created_at: z.string().optional(),
  html_url: z.url(),
  project_key: z.string(),
  provenance: SonarIntegrationProvenanceSchema.optional(),
  type: z.literal('sonar'),
  updated_at: z.string().optional(),
}).describe('SonarRepositoryIntegration')
export type SonarRepositoryIntegration = z.infer<typeof SonarRepositoryIntegrationSchema>
true satisfies AreEquivalent<SonarRepositoryIntegration, components['schemas']['SonarRepositoryIntegrationResponse']>

export const VeracodeRepositoryIntegrationSchema = RepositoryIntegrationResponseBaseSchema.extend({
  application_guid: z.string(),
  sandbox_guid: z.string().optional(),
  type: z.literal('veracode'),
}).describe('VeracodeRepositoryIntegration')
export type VeracodeRepositoryIntegration = z.infer<typeof VeracodeRepositoryIntegrationSchema>
true satisfies AreEquivalent<
  VeracodeRepositoryIntegration,
  components['schemas']['VeracodeRepositoryIntegrationResponse']
>

export const AppScanRepositoryIntegrationSchema = RepositoryIntegrationResponseBaseSchema.extend({
  application_id: z.string(),
  html_url: z.url(),
  type: z.literal('appscan'),
}).describe('AppScanRepositoryIntegration')
export type AppScanRepositoryIntegration = z.infer<typeof AppScanRepositoryIntegrationSchema>
true satisfies AreEquivalent<
  AppScanRepositoryIntegration,
  components['schemas']['AppScanRepositoryIntegrationResponse']
>

export const ArnicaRepositoryIntegrationSchema = RepositoryIntegrationResponseBaseSchema.extend({
  asset_key: z.string(),
  created_at: z.string(),
  provenance: ArnicaIntegrationProvenanceSchema,
  type: z.literal('arnica'),
  updated_at: z.string(),
}).describe('ArnicaRepositoryIntegration')
export type ArnicaRepositoryIntegration = z.infer<typeof ArnicaRepositoryIntegrationSchema>
true satisfies AreEquivalent<ArnicaRepositoryIntegration, components['schemas']['ArnicaRepositoryIntegrationResponse']>

export const DatadogRepositoryIntegrationSchema = RepositoryIntegrationResponseBaseSchema.extend({
  repository_id: z.string(),
  type: z.literal('datadog'),
}).describe('DatadogRepositoryIntegration')
export type DatadogRepositoryIntegration = z.infer<typeof DatadogRepositoryIntegrationSchema>
true satisfies AreEquivalent<
  DatadogRepositoryIntegration,
  components['schemas']['DatadogRepositoryIntegrationResponse']
>

const ProvenanceSchema = z.enum(['SARIF', 'USER_SUPPLIED']).describe('Provenance')
type Provenance = z.infer<typeof ProvenanceSchema>
true satisfies AreEquivalent<Provenance, components['schemas']['Provenance']>

export const PolarisRepositoryIntegrationSchema = RepositoryIntegrationResponseBaseSchema.extend({
  created_at: InstantSchema,
  polaris_project_id: z.string(),
  provenance: ProvenanceSchema,
  type: z.literal('polaris'),
}).describe('PolarisRepositoryIntegration')
export type PolarisRepositoryIntegration = z.infer<typeof PolarisRepositoryIntegrationSchema>
true satisfies AreEquivalent<
  PolarisRepositoryIntegration,
  components['schemas']['PolarisRepositoryIntegrationResponse']
>

export const RepositoryIntegrationSchema = z
  .discriminatedUnion('type', [
    SonarRepositoryIntegrationSchema,
    VeracodeRepositoryIntegrationSchema,
    CheckmarxRepositoryIntegrationSchema,
    AppScanRepositoryIntegrationSchema,
    ArnicaRepositoryIntegrationSchema,
    DatadogRepositoryIntegrationSchema,
    PolarisRepositoryIntegrationSchema,
  ])
  .describe('RepositoryIntegration')
export type RepositoryIntegration = z.infer<typeof RepositoryIntegrationSchema>
true satisfies AreEquivalent<RepositoryIntegration, components['schemas']['RepositoryIntegrationResponse']>

export const RepositoryIntegrationPaginatedResponseSchema = Paginated(
  RepositoryIntegrationSchema,
  StandardPageNavigationLinksSchema
).describe('RepositoryIntegrationPaginatedResponse')
export type RepositoryIntegrationPaginatedResponse = z.infer<typeof RepositoryIntegrationPaginatedResponseSchema>
true satisfies AreEquivalent<
  RepositoryIntegrationPaginatedResponse,
  components['schemas']['RepositoryIntegrationPaginatedResponse']
>

export const BitbucketWorkspaceSchema = z
  .object({
    _links: z.object({
      repositories: HALLinkSchema,
    }),
    name: z.string(),
    slug: z.string(),
  })
  .describe('BitbucketWorkspace')
export type BitbucketWorkspace = z.infer<typeof BitbucketWorkspaceSchema>
true satisfies AreEquivalent<BitbucketWorkspace, components['schemas']['BitbucketWorkspaceResponse']>

export const BitbucketWorkspacePaginatedResponseSchema = Paginated(
  BitbucketWorkspaceSchema,
  StandardPageNavigationLinksSchema
).describe('BitbucketWorkspacePaginatedResponse')
export type BitbucketWorkspacePaginatedResponse = z.infer<typeof BitbucketWorkspacePaginatedResponseSchema>
true satisfies AreEquivalent<
  BitbucketWorkspacePaginatedResponse,
  components['schemas']['BitbucketWorkspacePaginatedResponse']
>

export const IntegrationsBitbucketRepositoryResponseSchema = z
  .object({
    name: z.string(),
    pixee_id: z.string().optional(),
    slug: z.string(),
    workspace: z.string(),
  })
  .describe('IntegrationsBitbucketRepositoryResponse')
export type IntegrationsBitbucketRepositoryResponse = z.infer<typeof IntegrationsBitbucketRepositoryResponseSchema>
true satisfies AreEquivalent<
  IntegrationsBitbucketRepositoryResponse,
  components['schemas']['IntegrationsBitbucketRepositoryResponse']
>

export const BitbucketRepositoryPaginatedResponseSchema = Paginated(
  IntegrationsBitbucketRepositoryResponseSchema,
  StandardPageNavigationLinksSchema
).describe('BitbucketRepositoryPaginatedResponse')
export type BitbucketRepositoryPaginatedResponse = z.infer<typeof BitbucketRepositoryPaginatedResponseSchema>
true satisfies AreEquivalent<
  BitbucketRepositoryPaginatedResponse,
  components['schemas']['BitbucketRepositoryPaginatedResponse']
>

export const VerificationResultResponseSchema = z
  .enum(['likely-exploitable', 'not-exploitable', 'partially-exploitable', 'analysis-unavailable'])
  .describe('VerificationResultResponse')
export type VerificationResultResponse = z.infer<typeof VerificationResultResponseSchema>
true satisfies AreEquivalent<VerificationResultResponse, components['schemas']['VerificationResultResponse']>

export const ResultsVerificationFileResponseSchema = z
  .object({
    content: z.string(),
    filename: z.string(),
    title: z.string(),
  })
  .describe('ResultsVerificationFileResponse')
export type ResultsVerificationFileResponse = z.infer<typeof ResultsVerificationFileResponseSchema>
true satisfies AreEquivalent<ResultsVerificationFileResponse, components['schemas']['ResultsVerificationFileResponse']>

export const ResultsVerificationHeadlineResponseSchema = z
  .object({
    description: z.string(),
    title: z.string(),
  })
  .describe('ResultsVerificationHeadlineResponse')
export type ResultsVerificationHeadlineResponse = z.infer<typeof ResultsVerificationHeadlineResponseSchema>
true satisfies AreEquivalent<
  ResultsVerificationHeadlineResponse,
  components['schemas']['ResultsVerificationHeadlineResponse']
>

export const ResultsVerificationPayloadResultsResponseSchema = z
  .object({
    columns: z.array(z.string()),
    rows: z.array(z.record(z.string(), z.string())),
  })
  .describe('ResultsVerificationPayloadResultsResponse')
export type ResultsVerificationPayloadResultsResponse = z.infer<typeof ResultsVerificationPayloadResultsResponseSchema>
true satisfies AreEquivalent<
  ResultsVerificationPayloadResultsResponse,
  components['schemas']['ResultsVerificationPayloadResultsResponse']
>

export const ResultsVerificationResponseSchema = z
  .object({
    body: z.string(),
    code_blocks: z.array(ResultsVerificationFileResponseSchema),
    headlines: z.array(ResultsVerificationHeadlineResponseSchema),
    payload_results: ResultsVerificationPayloadResultsResponseSchema.optional(),
    result: VerificationResultResponseSchema,
  })
  .describe('ResultsVerificationResponse')
export type ResultsVerificationResponse = z.infer<typeof ResultsVerificationResponseSchema>
true satisfies AreEquivalent<ResultsVerificationResponse, components['schemas']['ResultsVerificationResponse']>

export const OrganizationPreferencesLinksSchema = z
  .object({
    self: HALLinkSchema,
  })
  .describe('OrganizationPreferencesLinks')
export type OrganizationPreferencesLinks = z.infer<typeof OrganizationPreferencesLinksSchema>
true satisfies AreEquivalent<OrganizationPreferencesLinks, components['schemas']['OrganizationPreferencesLinks']>

export const OrganizationPreferencesResponseSchema = z
  .object({
    _links: OrganizationPreferencesLinksSchema,
    content: z.string(),
    org_id: z.string(),
    updated_at: z.string(),
    updated_by: z.string(),
  })
  .describe('OrganizationPreferencesResponse')
export type OrganizationPreferencesResponse = z.infer<typeof OrganizationPreferencesResponseSchema>
true satisfies AreEquivalent<OrganizationPreferencesResponse, components['schemas']['OrganizationPreferencesResponse']>

export const UpdateOrganizationPreferencesRequestSchema = z
  .object({
    content: z.string().max(10_000),
  })
  .describe('UpdateOrganizationPreferencesRequest')
export type UpdateOrganizationPreferencesRequest = z.infer<typeof UpdateOrganizationPreferencesRequestSchema>
true satisfies AreEquivalent<
  UpdateOrganizationPreferencesRequest,
  components['schemas']['UpdateOrganizationPreferencesRequest']
>

// #endregion

// #region Request schemas

const WorkflowExecutionPolicySchema = z.enum(['execute', 'prevent-execution']).describe('WorkflowExecutionPolicy')
type WorkflowExecutionPolicy = z.infer<typeof WorkflowExecutionPolicySchema>
true satisfies AreEquivalent<WorkflowExecutionPolicy, components['schemas']['WorkflowExecutionPolicy']>

const BaseSchema = z
  .object({
    branch: z.string(),
    sha: z.string().optional(),
  })
  .describe('Base')
type Base = z.infer<typeof BaseSchema>
true satisfies AreEquivalent<Base, components['schemas']['Base']>

export const CreateScanRequestMetadataSchema = z.object({
  base: BaseSchema.optional(),
  branch: z.string().min(1).optional(),
  gitlab_pipeline_id: z.number().optional(),
  integration_id: z.string().optional(),
  pr_number: z.number().int().optional(),
  sha: z.string().optional(),
  tool: ToolSchema,
  workflow_execution_policy: WorkflowExecutionPolicySchema,
})
type CreateScanRequestMetadata = z.infer<typeof CreateScanRequestMetadataSchema>
true satisfies AreEquivalent<CreateScanRequestMetadata, components['schemas']['CreateScanRequestMetadata']>

export const CreateSbomRequestMetadataSchema = z
  .object({
    branch: z.string().min(1).optional(),
    sha: z.string(),
    tool: SbomToolSchema,
  })
  .describe('CreateSbomRequestMetadata')
export type CreateSbomRequestMetadata = z.infer<typeof CreateSbomRequestMetadataSchema>
true satisfies AreEquivalent<CreateSbomRequestMetadata, components['schemas']['CreateSbomRequestMetadata']>

export const PatchesCreatePatchRequestSchema = z
  .object({
    findings: FindingsSelectionSchema.optional(),
    fix_result_ids: z.array(z.string()).min(1).optional(),
  })
  .describe('PatchesCreatePatchRequest')
export type PatchesCreatePatchRequest = z.infer<typeof PatchesCreatePatchRequestSchema>
true satisfies AreEquivalent<PatchesCreatePatchRequest, components['schemas']['PatchesCreatePatchRequest']>

export const CreateWorkflowRequestSchema = z
  .object({
    action: WorkflowActionSchema,
    event: z.discriminatedUnion('type', [
      z.object({
        branch: z.string().optional(),
        cadence: ScheduleWorkflowCadenceSchema,
        start: z.iso.datetime().optional(),
        type: z.literal('schedule'),
      }),
      z.object({
        branch: z.string().optional(),
        type: z.literal('new-scan'),
      }),
      z.object({
        source_branch: z.string().optional(),
        target_branch: z.string().optional(),
        type: z.literal('pull-request-scan'),
      }),
    ]),
    name: z.string().min(1).optional(),
    tool: ToolSchema,
  })
  .describe('CreateWorkflowRequest')
export type CreateWorkflowRequest = z.infer<typeof CreateWorkflowRequestSchema>
// @ts-expect-error Spec uses oneOf union without an explicit type discriminator; Zod uses a discriminatedUnion with one.
true satisfies AreEquivalent<CreateWorkflowRequest, components['schemas']['CreateWorkflowRequest']>

export const UpdateScheduleWorkflowEventSchema = z
  .object({
    branch: z.string().optional(),
    cadence: ScheduleWorkflowCadenceSchema.optional(),
    start: z.string().nullish(),
    type: z.literal('schedule'),
  })
  .describe('UpdateScheduleWorkflowEvent')
export type UpdateScheduleWorkflowEvent = z.infer<typeof UpdateScheduleWorkflowEventSchema>
true satisfies AreEquivalent<UpdateScheduleWorkflowEvent, components['schemas']['UpdateScheduleWorkflowEvent']>

export const UpdateNewScanWorkflowEventSchema = z
  .object({
    branch: z.string().optional(),
    type: z.literal('new-scan'),
  })
  .describe('UpdateNewScanWorkflowEvent')
export type UpdateNewScanWorkflowEvent = z.infer<typeof UpdateNewScanWorkflowEventSchema>
true satisfies AreEquivalent<UpdateNewScanWorkflowEvent, components['schemas']['UpdateNewScanWorkflowEvent']>

export const UpdatePullRequestScanWorkflowEventSchema = z
  .object({
    source_branch: z.string().nullish(),
    target_branch: z.string().nullish(),
    type: z.literal('pull-request-scan'),
  })
  .describe('UpdatePullRequestScanWorkflowEvent')
export type UpdatePullRequestScanWorkflowEvent = z.infer<typeof UpdatePullRequestScanWorkflowEventSchema>
true satisfies AreEquivalent<
  UpdatePullRequestScanWorkflowEvent,
  components['schemas']['UpdatePullRequestScanWorkflowEvent']
>

export const UpdateNoneWorkflowActionSchema = z
  .object({
    type: z.literal('none'),
  })
  .describe('UpdateNoneWorkflowAction')
export type UpdateNoneWorkflowAction = z.infer<typeof UpdateNoneWorkflowActionSchema>
true satisfies AreEquivalent<UpdateNoneWorkflowAction, components['schemas']['UpdateNoneWorkflowAction']>

export const UpdateCreatePatchWorkflowActionSchema = z
  .object({
    findings: z
      .object({
        include_severity_labels: z.array(z.string()).nullable().optional(),
        maximum_severity_score: z.number().int().nullable().optional(),
        minimum_severity_score: z.number().int().nullable().optional(),
      })
      .nullable()
      .optional(),
    fixes: z
      .object({
        minimum_fix_confidence: ConfidenceSchema.nullable().optional(),
      })
      .nullable()
      .optional(),
    limit: z
      .union([z.object({ max: z.number().int(), type: z.literal('finding-count') }), z.literal('INSTANCE')])
      .nullable()
      .optional(),
    type: z.literal('create-patch'),
  })
  .describe('UpdateCreatePatchWorkflowAction')
export type UpdateCreatePatchWorkflowAction = z.infer<typeof UpdateCreatePatchWorkflowActionSchema>
// @ts-expect-error Nullable oneOf in OpenAPI produces collapsed intersection type for limit field.
true satisfies AreEquivalent<UpdateCreatePatchWorkflowAction, components['schemas']['UpdateCreatePatchWorkflowAction']>

export const UpdateWorkflowActionSchema = z
  .discriminatedUnion('type', [UpdateCreatePatchWorkflowActionSchema, UpdateNoneWorkflowActionSchema])
  .describe('UpdateWorkflowAction')
export type UpdateWorkflowAction = z.infer<typeof UpdateWorkflowActionSchema>
// @ts-expect-error Nullable oneOf in OpenAPI produces collapsed intersection type for limit field.
true satisfies AreEquivalent<UpdateWorkflowAction, components['schemas']['UpdateWorkflowAction']>

export const UpdateWorkflowEventSchema = z
  .discriminatedUnion('type', [
    UpdateScheduleWorkflowEventSchema,
    UpdateNewScanWorkflowEventSchema,
    UpdatePullRequestScanWorkflowEventSchema,
  ])
  .describe('UpdateWorkflowEvent')
export type UpdateWorkflowEvent = z.infer<typeof UpdateWorkflowEventSchema>
true satisfies AreEquivalent<UpdateWorkflowEvent, NonNullable<components['schemas']['UpdateWorkflowRequest']['event']>>

export const UpdateWorkflowRequestSchema = z
  .object({
    action: UpdateWorkflowActionSchema.optional(),
    enabled: z.boolean().optional(),
    event: UpdateWorkflowEventSchema.optional(),
    name: z.string().optional(),
  })
  .describe('UpdateWorkflowRequest')
export type UpdateWorkflowRequest = z.infer<typeof UpdateWorkflowRequestSchema>
// @ts-expect-error Nullable oneOf in OpenAPI produces collapsed intersection type for limit field in action variant.
true satisfies AreEquivalent<UpdateWorkflowRequest, components['schemas']['UpdateWorkflowRequest']>

export const AddGitRepositoryRequestSchema = z
  .object({
    credentials: z.object({
      password: z.string(),
      username: z.string(),
    }),
    name: z.string(),
    type: z.literal('git'),
    url: z.url(),
  })
  .describe('AddGitRepositoryRequest')
export type AddGitRepositoryRequest = z.infer<typeof AddGitRepositoryRequestSchema>
true satisfies AreEquivalent<AddGitRepositoryRequest, components['schemas']['CreateGitRepositoryRequest']>

export const AddAzureRepositoryRequestSchema = z
  .object({
    name: z.string(),
    organization: z.string(),
    project: z.string(),
    type: z.literal('azure'),
  })
  .describe('AddAzureRepositoryRequest')
export type AddAzureRepositoryRequest = z.infer<typeof AddAzureRepositoryRequestSchema>
true satisfies AreEquivalent<AddAzureRepositoryRequest, components['schemas']['CreateAzureRepositoryRequest']>

export const AddGitLabRepositoryRequestSchema = z
  .object({
    name: z.string(),
    namespace: z.string(),
    type: z.literal('gitlab'),
  })
  .describe('AddGitLabRepositoryRequest')
export type AddGitLabRepositoryRequest = z.infer<typeof AddGitLabRepositoryRequestSchema>
true satisfies AreEquivalent<AddGitLabRepositoryRequest, components['schemas']['CreateGitLabRepositoryRequest']>

export const AddBitbucketRepositoryRequestSchema = z
  .object({
    slug: z.string(),
    type: z.literal('bitbucket'),
    workspace: z.string(),
  })
  .describe('AddBitbucketRepositoryRequest')
export type AddBitbucketRepositoryRequest = z.infer<typeof AddBitbucketRepositoryRequestSchema>
true satisfies AreEquivalent<AddBitbucketRepositoryRequest, components['schemas']['CreateBitbucketRepositoryRequest']>

export const AddRepositoryRequestSchema = z
  .discriminatedUnion('type', [
    AddGitRepositoryRequestSchema,
    AddAzureRepositoryRequestSchema,
    AddGitLabRepositoryRequestSchema,
    AddBitbucketRepositoryRequestSchema,
  ])
  .describe('AddRepositoryRequest')
export type AddRepositoryRequest = z.infer<typeof AddRepositoryRequestSchema>
true satisfies AreEquivalent<AddRepositoryRequest, components['schemas']['CreateRepositoryRequest']>

export const CreateSonarRepositoryIntegrationRequestSchema = z
  .object({
    project_key: z.string(),
    type: z.literal('sonar'),
  })
  .describe('CreateSonarRepositoryIntegrationRequest')
type CreateSonarRepositoryIntegrationRequest = z.infer<typeof CreateSonarRepositoryIntegrationRequestSchema>
true satisfies AreEquivalent<
  CreateSonarRepositoryIntegrationRequest,
  components['schemas']['CreateSonarRepositoryIntegrationRequest']
>

export const CreateVeracodeRepositoryIntegrationRequestSchema = z
  .object({
    application_guid: z.string(),
    sandbox_guid: z.string().optional(),
    type: z.literal('veracode'),
  })
  .describe('CreateVeracodeRepositoryIntegrationRequest')
type CreateVeracodeRepositoryIntegrationRequest = z.infer<typeof CreateVeracodeRepositoryIntegrationRequestSchema>
true satisfies AreEquivalent<
  CreateVeracodeRepositoryIntegrationRequest,
  components['schemas']['CreateVeracodeRepositoryIntegrationRequest']
>

export const CreateCheckmarxRepositoryIntegrationRequestSchema = z
  .object({
    project_id: z.string(),
    type: z.literal('checkmarx'),
  })
  .describe('CreateCheckmarxRepositoryIntegrationRequest')
type CreateCheckmarxRepositoryIntegrationRequest = z.infer<typeof CreateCheckmarxRepositoryIntegrationRequestSchema>
true satisfies AreEquivalent<
  CreateCheckmarxRepositoryIntegrationRequest,
  components['schemas']['CreateCheckmarxRepositoryIntegrationRequest']
>

export const CreateAppScanRepositoryIntegrationRequestSchema = z
  .object({
    application_id: z.string(),
    type: z.literal('appscan'),
  })
  .describe('CreateAppScanRepositoryIntegrationRequest')
type CreateAppScanRepositoryIntegrationRequest = z.infer<typeof CreateAppScanRepositoryIntegrationRequestSchema>
true satisfies AreEquivalent<
  CreateAppScanRepositoryIntegrationRequest,
  components['schemas']['CreateAppScanRepositoryIntegrationRequest']
>

export const CreateArnicaRepositoryIntegrationRequestSchema = z
  .object({
    asset_key: z.string(),
    type: z.literal('arnica'),
  })
  .describe('CreateArnicaRepositoryIntegrationRequest')
type CreateArnicaRepositoryIntegrationRequest = z.infer<typeof CreateArnicaRepositoryIntegrationRequestSchema>
true satisfies AreEquivalent<
  CreateArnicaRepositoryIntegrationRequest,
  components['schemas']['CreateArnicaRepositoryIntegrationRequest']
>

export const CreateDatadogRepositoryIntegrationRequestSchema = z
  .object({
    integration_id: z.string().optional(),
    repository_id: z.string(),
    type: z.literal('datadog'),
  })
  .describe('CreateDatadogRepositoryIntegrationRequest')
type CreateDatadogRepositoryIntegrationRequest = z.infer<typeof CreateDatadogRepositoryIntegrationRequestSchema>
true satisfies AreEquivalent<
  CreateDatadogRepositoryIntegrationRequest,
  components['schemas']['CreateDatadogRepositoryIntegrationRequest']
>

export const CreatePolarisRepositoryIntegrationRequestSchema = z
  .object({
    polaris_project_id: z.string(),
    type: z.literal('polaris'),
  })
  .describe('CreatePolarisRepositoryIntegrationRequest')
type CreatePolarisRepositoryIntegrationRequest = z.infer<typeof CreatePolarisRepositoryIntegrationRequestSchema>
true satisfies AreEquivalent<
  CreatePolarisRepositoryIntegrationRequest,
  components['schemas']['CreatePolarisRepositoryIntegrationRequest']
>

export const CreateRepositoryIntegrationRequestSchema = z
  .discriminatedUnion('type', [
    CreateSonarRepositoryIntegrationRequestSchema,
    CreateVeracodeRepositoryIntegrationRequestSchema,
    CreateCheckmarxRepositoryIntegrationRequestSchema,
    CreateAppScanRepositoryIntegrationRequestSchema,
    CreateArnicaRepositoryIntegrationRequestSchema,
    CreateDatadogRepositoryIntegrationRequestSchema,
    CreatePolarisRepositoryIntegrationRequestSchema,
  ])
  .describe('CreateRepositoryIntegrationRequest')
export type CreateRepositoryIntegrationRequest = z.infer<typeof CreateRepositoryIntegrationRequestSchema>
true satisfies AreEquivalent<
  CreateRepositoryIntegrationRequest,
  components['schemas']['CreateRepositoryIntegrationRequest']
>

// #endregion

// #region Feedback schemas (ISS-6528)

export const FeedbackRatingSchema = z.enum(['positive', 'negative']).describe('FeedbackRating')
export type FeedbackRating = z.infer<typeof FeedbackRatingSchema>
true satisfies AreEquivalent<FeedbackRating, components['schemas']['FeedbackRating']>

export const FeedbackSubmissionResponseSchema = z
  .object({
    createdAt: InstantSchema.optional(),
    id: z.string().optional(),
    rating: FeedbackRatingSchema.optional(),
    text: z.string().optional(),
    username: z.string().optional(),
  })
  .describe('FeedbackSubmissionResponse')
export type FeedbackSubmissionResponse = z.infer<typeof FeedbackSubmissionResponseSchema>
true satisfies AreEquivalent<FeedbackSubmissionResponse, components['schemas']['FeedbackSubmissionResponse']>

export const FeedbackSubmissionPaginatedResponseSchema = Paginated(
  FeedbackSubmissionResponseSchema,
  StandardPageNavigationLinksSchema
).describe('FeedbackSubmissionPaginatedResponse')
export type FeedbackSubmissionPaginatedResponse = z.infer<typeof FeedbackSubmissionPaginatedResponseSchema>
true satisfies AreEquivalent<
  FeedbackSubmissionPaginatedResponse,
  components['schemas']['FeedbackSubmissionPaginatedResponse']
>

export const CreateFeedbackSubmissionRequestSchema = z
  .object({
    rating: FeedbackRatingSchema,
    text: z.string().max(1750).optional(),
  })
  .describe('CreateFeedbackSubmissionRequest')
export type CreateFeedbackSubmissionRequest = z.infer<typeof CreateFeedbackSubmissionRequestSchema>
true satisfies AreEquivalent<CreateFeedbackSubmissionRequest, components['schemas']['CreateFeedbackSubmissionRequest']>

export const FeedbackSubmissionsDeleteDisabledProblemResponseSchema = z
  .object({
    detail: z.string(),
    status: z.number().int(),
    title: z.string(),
  })
  .describe('FeedbackSubmissionsDeleteDisabledProblemResponse')
export type FeedbackSubmissionsDeleteDisabledProblemResponse = z.infer<
  typeof FeedbackSubmissionsDeleteDisabledProblemResponseSchema
>
true satisfies AreEquivalent<
  FeedbackSubmissionsDeleteDisabledProblemResponse,
  components['schemas']['FeedbackSubmissionsDeleteDisabledProblemResponse']
>

export const CodeFetchFailedProblemSchema = z
  .object({
    detail: z.string(),
    status: z.number().int(),
    title: z.string(),
  })
  .describe('CodeFetchFailedProblem')
export type CodeFetchFailedProblem = z.infer<typeof CodeFetchFailedProblemSchema>
true satisfies AreEquivalent<CodeFetchFailedProblem, components['schemas']['CodeFetchFailedProblem']>

export const CodingGuidelinesLinksSchema = z
  .object({
    content: HALLinkSchema,
    events: HALLinkSchema,
    self: HALLinkSchema,
  })
  .describe('CodingGuidelinesLinks')
export type CodingGuidelinesLinks = z.infer<typeof CodingGuidelinesLinksSchema>
true satisfies AreEquivalent<CodingGuidelinesLinks, components['schemas']['CodingGuidelinesLinks']>

export const CodingGuidelinesResponseSchema = z
  .object({
    _links: CodingGuidelinesLinksSchema.optional(),
    content_type: z.string().optional(),
    display_name: z.string().optional(),
    filename: z.string().optional(),
    id: z.string().optional(),
    size: z.number().int().optional(),
    status: z.string().optional(),
    updated_at: InstantSchema.optional(),
    uploaded_at: InstantSchema.optional(),
    uploaded_by: z.string().optional(),
  })
  .describe('CodingGuidelinesResponse')
export type CodingGuidelinesResponse = z.infer<typeof CodingGuidelinesResponseSchema>
true satisfies AreEquivalent<CodingGuidelinesResponse, components['schemas']['CodingGuidelinesResponse']>

export const UpdateCodingGuidelinesRequestSchema = z
  .object({ display_name: z.string().regex(/\S/).max(100) })
  .describe('UpdateCodingGuidelinesRequest')
export type UpdateCodingGuidelinesRequest = z.infer<typeof UpdateCodingGuidelinesRequestSchema>
true satisfies AreEquivalent<UpdateCodingGuidelinesRequest, components['schemas']['UpdateCodingGuidelinesRequest']>

export const FeedbackSubmissionsDeleteResponseSchema = z.object({
  deleted_feedback_submissions: z.number().int(),
  deleted_judgments: z.number().int(),
  deleted_memories: z.number().int(),
})
export type FeedbackSubmissionsDeleteResponse = z.infer<typeof FeedbackSubmissionsDeleteResponseSchema>
true satisfies AreEquivalent<
  FeedbackSubmissionsDeleteResponse,
  components['schemas']['FeedbackSubmissionsDeleteResponse']
>

// #endregion

// #region Support Bundle schemas (ISS-7005, Pixee Enterprise Server)
//
// TODO(ISS-7598): SupportBundleResponse and SupportBundleAlreadyRunningProblemResponse are served by
// pixee-platform's REST API and should be defined in its OpenAPI spec, not hand-written here.
// TODO(ISS-7599): once ISS-7598 lands, re-sync these schemas against components['schemas']
// (satisfies z.ZodType + AreEquivalent).

const SupportBundleLinksSchema = z
  .object({
    events: HALLinkSchema,
    self: HALLinkSchema,
  })
  .describe('SupportBundleLinks')

export const SupportBundleResponseSchema = z
  .object({
    _links: SupportBundleLinksSchema,
    id: z.string(),
    phase: z.string(),
  })
  .describe('SupportBundleResponse')
export type SupportBundleResponse = z.infer<typeof SupportBundleResponseSchema>

/**
 * Tier 3 extension of the platform's problem-JSON for the 409 already-running response. The
 * platform copies `submitter` from the in-cluster service's Job annotation so we can render it in
 * the UI.
 */
export const SupportBundleAlreadyRunningProblemResponseSchema = z
  .object({
    detail: z.string(),
    id: z.string(),
    phase: z.string(),
    submitter: z.string(),
    title: z.string(),
    type: z.literal('pixee:problems/api/support-bundles/already-running'),
  })
  .describe('SupportBundleAlreadyRunningProblemResponse')
export type SupportBundleAlreadyRunningProblemResponse = z.infer<
  typeof SupportBundleAlreadyRunningProblemResponseSchema
>

export const CapabilityDisabledProblemResponseSchema = z
  .object({
    detail: z.string(),
    status: z.number().int(),
    title: z.string(),
  })
  .describe('CapabilityDisabledProblemResponse')
export type CapabilityDisabledProblemResponse = z.infer<typeof CapabilityDisabledProblemResponseSchema>
true satisfies AreEquivalent<
  CapabilityDisabledProblemResponse,
  components['schemas']['CapabilityDisabledProblemResponse']
>

// #endregion

// #region API Root schemas (ISS-7005 — drives capability discovery)
//
// TODO(ISS-7598): the API root response and its _links (including the optional support-bundles link)
// should be defined in the pixee-platform OpenAPI spec.
// TODO(ISS-7599): once ISS-7598 lands, re-sync ApiRootResponse/ApiRootLinks against
// components['schemas'] and drop the .loose() escape hatch.

/**
 * `_links` on the root API response. `support-bundles` is present only when the chart's
 * supportBundleApi.enabled is true (and the in-cluster Service is rendered).
 */
const ApiRootLinksSchema = z
  .object({
    self: HALLinkSchema,
    'support-bundles': HALLinkSchema.optional(),
  })
  .loose()

export const ApiRootResponseSchema = z
  .object({
    _links: ApiRootLinksSchema,
  })
  .describe('ApiRootResponse')
export type ApiRootResponse = z.infer<typeof ApiRootResponseSchema>

export const PatchFixApplicationStatusResponseSchema = z.enum(['pending', 'applied', 'dropped'])
export type PatchFixApplicationStatusResponse = z.infer<typeof PatchFixApplicationStatusResponseSchema>
true satisfies AreEquivalent<
  PatchFixApplicationStatusResponse,
  components['schemas']['PatchFixApplicationStatusResponse']
>

export const PatchesPatchFixResponseSchema = z.object({
  _links: PatchesPatchFixLinksSchema,
  application_status: PatchFixApplicationStatusResponseSchema,
  merge_notes: z.string().optional(),
})
export type PatchesPatchFixResponse = z.infer<typeof PatchesPatchFixResponseSchema>
true satisfies AreEquivalent<PatchesPatchFixResponse, components['schemas']['PatchesPatchFixResponse']>

export const PageEmbeddedPatchFixResponseSchema = z.object({
  items: z.array(PatchesPatchFixResponseSchema),
})
export type PageEmbeddedPatchFixResponse = z.infer<typeof PageEmbeddedPatchFixResponseSchema>
true satisfies AreEquivalent<PageEmbeddedPatchFixResponse, components['schemas']['PageEmbeddedPatchesPatchFixResponse']>

export const PatchFixPaginatedResponseSchema = Paginated(
  PatchesPatchFixResponseSchema,
  StandardPageNavigationLinksSchema
).describe('PatchFixPaginatedResponse')
export type PatchFixPaginatedResponse = z.infer<typeof PatchFixPaginatedResponseSchema>
true satisfies AreEquivalent<PatchFixPaginatedResponse, components['schemas']['PatchFixPaginatedResponse']>

// #endregion
